/** @jsxImportSource preact */
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import {
  type RefundQuoteData,
  type BookingFinancialInputs,
  CANCELLATION_REASONS,
  COUPON_OPEN_CODES,
  computeRefundQuoteStatement,
  emptyConfidenceReportLog,
  hasForeignCurrencyPenalty,
  isAwaitingFareRules,
  isQuoteSettled,
  lockConfidenceReports,
  mergeRefundQuotePayload,
  withConjunctionTicketKeys,
  recordConfidenceReport,
  roundCurrency,
  shouldReportConfidence,
  warningCodes,
  withoutExchangedTickets,
  type ConfidenceReportLog,
  type ConfidenceReportReason,
  applyWaiverToConfidence,
  evaluateWaiver,
  waiverFeeFor,
  type CarrierSurchargeTable,
  type ScoringSettings,
  type TicketStatusSummary,
  type WaiverCheckState,
} from '@plt/refund-quote'
import { config } from '../config'
import { getLiveScoring, lookupWaiver, type StoredWaiver } from './refund-scoring-api'
import { getLiveCarrierSurcharges } from './refund-surcharge-api'
import { NdcCancelQuotePanel } from './NdcCancelQuotePanel'
import { JetstarCancellationRules } from './JetstarCancellationRules'
import { QuoteLoadingPanel } from './QuoteLoadingPanel'
import { retrieveRefundQuote } from './retrieve-refund-quote'
import { isJetstarLccBooking, JETSTAR_AGENT_HUB_MESSAGE } from './jetstar-cancellation-rules'
import { useAuth } from '../providers/auth'
import { extractBookingId, isFrontEmbedded, useFrontContext } from '../providers/front'

/** Every cancel-execution action is the same POST with the same error shape — say it once. */
async function postCancelExecution(bookingId: string, action: string): Promise<Record<string, unknown>> {
  const resp = await fetch(`${config.apiBaseUrl}/bookings/${bookingId}/cancel-execution/${action}`, {
    method: 'POST',
    credentials: 'include',
  })
  const data = await resp.json().catch(() => ({}))
  if (!resp.ok) throw new Error((data as { error?: string }).error ?? `Request failed (${resp.status})`)
  return data as Record<string, unknown>
}

function messageFrom(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error'
}

interface BookingDetails extends BookingFinancialInputs {
  grossFareCost?: number
  cancelationPenalty?: number
  pnr?: string
  possibleToQuickResolve?: boolean
  ticketIssuedDateTime?: string
  invoiceDate?: string
  ticketStatus?: string
}

const WAIVER_CHECK_LABEL: Record<WaiverCheckState, string> = {
  met: 'Met',
  'not-met': 'Not met',
  unknown: 'Unknown',
  'agent-to-confirm': 'Confirm',
}

const WAIVER_CHECK_STYLE: Record<WaiverCheckState, string> = {
  met: 'bg-green-100 text-green-800',
  'not-met': 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-700',
  'agent-to-confirm': 'bg-amber-100 text-amber-800',
}

/** How long a quote stays open before its level is saved provisionally, if the fare rules have not settled it. */
const CONFIDENCE_FALLBACK_MS = 20_000

// The fare-rules request usually answers in a few seconds. Past this the quote shows without them, flagged.
const FARE_RULES_TIMEOUT_MS = 30_000

const COUPON_LABEL: Record<string, string> = {
  O: 'Open',
  I: 'Open',
  F: 'Flown',
  B: 'Flown',
  L: 'Lifted',
  R: 'Refunded',
  RF: 'Refunded',
  E: 'Exchanged',
  V: 'Voided',
  C: 'Checked in',
  A: 'Airport control',
  S: 'Suspended',
  P: 'Printed',
}

/** What the booking says about a ticket, shown even when Sabre's refund check could not assess it. */
function TicketStatusLine({ summary }: { summary: TicketStatusSummary }) {
  const coupons = summary.coupons.map(c => ({
    label: (c.code && COUPON_LABEL[c.code]) ?? c.status,
    open: !!c.code && COUPON_OPEN_CODES.has(c.code),
  }))

  return (
    <p className="text-xs text-gray-600 flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>Ticket {summary.status.toLowerCase()}</span>
      {coupons.length === 0 ? (
        <span className="text-amber-700">Coupon status not available</span>
      ) : (
        coupons.map((c, i) => (
          <span
            key={i}
            className={`px-1.5 py-0.5 rounded ${c.open ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
          >
            C{i + 1}: {c.label}
          </span>
        ))
      )}
    </p>
  )
}

export function Refunds() {
  const [bookingId, setBookingId] = useState('')
  const [fetchingBooking, setFetchingBooking] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  useAuth()

  // Booking-level fields
  const [salesPrice, setSalesPrice] = useState('')
  const [totalPaidAmount, setTotalPaidAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null)

  // Pax breakdown
  const [adultsCount, setAdultsCount] = useState('')
  const [childrenCount, setChildrenCount] = useState('')

  // Net Fare per pax
  const [netFareAdult, setNetFareAdult] = useState('')
  const [netFareChild, setNetFareChild] = useState('')
  const [netFareTtvEstimate, setNetFareTtvEstimate] = useState(false)
  const [isNetFareManuallyEdited, setIsNetFareManuallyEdited] = useState(false)

  // Gross airline refund ("Airline Refund to PLT") — override buffer; decision A: the entered
  // figure is the GROSS refund before the consolidator fee, from which deductions are implied.
  const [supplierCashRefund, setSupplierCashRefund] = useState('')
  const [isSupplierCashRefundOverridden, setIsSupplierCashRefundOverridden] = useState(false)

  // Airline Cancellation Fee override (active when ATPCO estimate fallback is used)
  const [airlineCancellationFeeOverride, setAirlineCancellationFeeOverride] = useState('')

  // PLT fee override
  const [isPltFeeOverridden, setIsPltFeeOverridden] = useState(false)
  const [pltFeeOverride, setPltFeeOverride] = useState('')

  // Airline admin fee (out-of-GDS — charged by airline separately from automated refund)
  const [adminFee, setAdminFee] = useState(0)
  const [isAdminFeeOverridden, setIsAdminFeeOverridden] = useState(false)

  // Per-ticket selection for partial cancellations
  const [selectedTicketNumbers, setSelectedTicketNumbers] = useState<Set<string>>(new Set())

  const [cancellationReason, setCancellationReason] = useState('')
  // Supplier email cancellation reason type — Voluntary/Involuntary, required before processing
  const [supplierNotificationReason, setSupplierNotificationReason] = useState<'voluntary' | 'involuntary' | ''>('')
  const [copySuccess, setCopySuccess] = useState(false)

  // Refund quote state
  const [refundQuoteData, setRefundQuoteData] = useState<RefundQuoteData | null>(null)
  const [refundQuoteLoading, setRefundQuoteLoading] = useState(false)
  const [fareRulesLoading, setFareRulesLoading] = useState(false)
  const [refundQuoteError, setRefundQuoteError] = useState('')
  const [refundConditionsExpanded, setRefundConditionsExpanded] = useState(true)
  const [fareRulesExpanded, setFareRulesExpanded] = useState(false)
  const [refundTotalsExpanded, setRefundTotalsExpanded] = useState(false)
  const [financialBreakdownExpanded, setFinancialBreakdownExpanded] = useState(false)

  // AI Assisted Quote — on-demand live LLM extraction of the already-loaded fare-rule text
  const [aiQuoteLoading, setAiQuoteLoading] = useState(false)
  const [aiQuoteError, setAiQuoteError] = useState('')
  const [aiQuoteFailures, setAiQuoteFailures] = useState<
    Array<{ originDestination: string; fareBasis: string; error: string }>
  >([])
  const [aiQuoteApplied, setAiQuoteApplied] = useState(false)

  // Void window
  const [isVoidWindow, setIsVoidWindow] = useState(false)
  // The quote's void figures and type stop being true at the deadline; only a new quote recomputes them.
  const [voidWindowExpired, setVoidWindowExpired] = useState(false)
  const [voidIssuedLabel, setVoidIssuedLabel] = useState('')
  const [voidDeadlineLabel, setVoidDeadlineLabel] = useState('')

  // Process cancellation state
  const [processCancellationLoading, setProcessCancellationLoading] = useState(false)
  const [processCancellationConfirm, setProcessCancellationConfirm] = useState(false)
  const [processCancellationError, setProcessCancellationError] = useState('')
  const [processCancellationSuccess, setProcessCancellationSuccess] = useState(false)
  // The type actually sent: a quote reload re-selects every ticket, so the live value can differ after submit.
  const [submittedCancellationType, setSubmittedCancellationType] = useState<'void' | 'full' | 'partial' | null>(null)
  // Likewise: the checkbox stays enabled while the request is in flight.
  const [submittedNoRefundExpected, setSubmittedNoRefundExpected] = useState(false)
  const [forceWorkflowOverride, setForceWorkflowOverride] = useState(false)
  const [workflowStep, setWorkflowStep] = useState<string | null>(null)
  const [refundStatus, setRefundStatus] = useState<string | null>(null)
  const [workflowStepLoading, setWorkflowStepLoading] = useState(false)
  const [workflowStatusError, setWorkflowStatusError] = useState('')
  const [closeRefundLoading, setCloseRefundLoading] = useState(false)
  const [closeRefundSuccess, setCloseRefundSuccess] = useState(false)
  const [closeRefundError, setCloseRefundError] = useState('')
  const [confirmRefundLoading, setConfirmRefundLoading] = useState(false)
  const [confirmRefundSuccess, setConfirmRefundSuccess] = useState(false)
  const [confirmRefundError, setConfirmRefundError] = useState('')
  const [forceCompleteRefundLoading, setForceCompleteRefundLoading] = useState(false)
  const [forceCompleteRefundSuccess, setForceCompleteRefundSuccess] = useState(false)
  const [forceCompleteRefundError, setForceCompleteRefundError] = useState('')
  const [recheckRefundLoading, setRecheckRefundLoading] = useState(false)
  const [recheckRefundResult, setRecheckRefundResult] = useState<{ refunded: boolean; detail: string } | null>(null)
  const [recheckRefundError, setRecheckRefundError] = useState('')
  const [confidenceOverrideOpen, setConfidenceOverrideOpen] = useState(false)
  const [confidenceOverrideLevel, setConfidenceOverrideLevel] = useState<'high' | 'medium' | 'low' | 'none'>('high')
  const [confidenceOverrideLoading, setConfidenceOverrideLoading] = useState(false)
  const [confidenceOverrideSuccess, setConfidenceOverrideSuccess] = useState(false)
  const [confidenceOverrideError, setConfidenceOverrideError] = useState('')

  // Waiver code — optional, passed to Sabre and included in supplier email
  const [waiverCode, setWaiverCode] = useState('')
  // The live scoring version from the Ops App; until it loads (or if it cannot) quotes use the package defaults.
  const [scoring, setScoring] = useState<ScoringSettings | undefined>(undefined)
  const [carrierSurcharges, setCarrierSurcharges] = useState<CarrierSurchargeTable | undefined>(undefined)
  // A registered waiver matching the code the quote was retrieved with. It changes the score only.
  const [registeredWaiver, setRegisteredWaiver] = useState<StoredWaiver | null>(null)
  const [waiverLookupLoading, setWaiverLookupLoading] = useState(false)
  const [waiverLookupMessage, setWaiverLookupMessage] = useState('')
  const [noCodeshareConfirmed, setNoCodeshareConfirmed] = useState(false)

  // Email override state — unchecked by default while in testing
  const [sendSupplierEmail, setSendSupplierEmail] = useState(true)
  const [supplierEmailOverride, setSupplierEmailOverride] = useState('')
  // The address auto-resolved from the ticketing PCC when the quote loaded. The field must never
  // be left blank — clearing it silently downgrades an enabled notification to a no-op send — so
  // an empty override snaps back to this on blur; only the checkbox above controls send/don't-send.
  const [supplierEmailDefault, setSupplierEmailDefault] = useState('')
  const [sendCustomerEmail, setSendCustomerEmail] = useState(false)
  const [customerEmailOverride, setCustomerEmailOverride] = useState('')
  // Default cancellations and zero-refund quotes have nothing to collect from the supplier. Saying
  // so up front keeps the booking out of refund monitoring, which would otherwise poll Sabre for
  // weeks and hand ops a review ticket it can only close by hand.
  const [noRefundExpected, setNoRefundExpected] = useState(false)

  // The booking every in-flight request was fired for. The async fetches below compare against
  // this before writing state, so a slow fare-rules or AI response for booking A can't land on B.
  const activeBookingIdRef = useRef('')

  // Every field below is scoped to one booking, and changing the Booking ID clears all of them
  // here. This used to be split between this effect and fetchBookingDetails, and the two lists
  // drifted — the AI-quote banners, the force-workflow override and the email overrides survived
  // into the next booking. Anything NOT reset here is session-scoped on purpose: the accordion
  // positions, the two send-email checkboxes, and copySuccess (self-clearing).
  const resetBookingContext = useCallback(() => {
    setBookingDetails(null)
    setErrorMessage('')
    setFetchingBooking(false)
    setCurrency('USD')
    setSalesPrice('')
    setTotalPaidAmount('')
    setAdultsCount('')
    setChildrenCount('')

    setRefundQuoteData(null)
    setRefundQuoteError('')
    setRefundQuoteLoading(false)
    setFareRulesLoading(false)
    setSelectedTicketNumbers(new Set())

    setAiQuoteApplied(false)
    setAiQuoteError('')
    setAiQuoteFailures([])
    setAiQuoteLoading(false)

    setIsVoidWindow(false)
    setVoidWindowExpired(false)
    setVoidIssuedLabel('')
    setVoidDeadlineLabel('')

    setNetFareAdult('')
    setNetFareChild('')
    setNetFareTtvEstimate(false)
    setIsNetFareManuallyEdited(false)

    setSupplierCashRefund('')
    setIsSupplierCashRefundOverridden(false)
    setAirlineCancellationFeeOverride('')
    setIsPltFeeOverridden(false)
    setPltFeeOverride('')
    setAdminFee(0)
    setIsAdminFeeOverridden(false)

    setProcessCancellationLoading(false)
    setProcessCancellationConfirm(false)
    setProcessCancellationError('')
    setProcessCancellationSuccess(false)
    setSubmittedCancellationType(null)
    setSubmittedNoRefundExpected(false)
    setForceWorkflowOverride(false)
    setWorkflowStep(null)
    setRefundStatus(null)
    setWorkflowStepLoading(false)
    setConfirmRefundLoading(false)
    setConfirmRefundSuccess(false)
    setConfirmRefundError('')
    setForceCompleteRefundLoading(false)
    setForceCompleteRefundSuccess(false)
    setForceCompleteRefundError('')
    setRecheckRefundLoading(false)
    setRecheckRefundResult(null)
    setRecheckRefundError('')
    setConfidenceOverrideOpen(false)
    setConfidenceOverrideLoading(false)
    setConfidenceOverrideSuccess(false)
    setConfidenceOverrideError('')
    setCancellationReason('')
    setSupplierNotificationReason('')
    setWaiverCode('')
    setRegisteredWaiver(null)
    setWaiverLookupMessage('')
    setNoCodeshareConfirmed(false)

    setSupplierEmailOverride('')
    setSupplierEmailDefault('')
    setCustomerEmailOverride('')
    setNoRefundExpected(false)
    setWorkflowStatusError('')
    setCloseRefundError('')
    setCloseRefundSuccess(false)
  }, [])

  useEffect(() => {
    if (frontContext) {
      const extracted = extractBookingId(frontContext)
      setBookingId(extracted ?? '')
    }
  }, [frontContext])

  // Populate-only — resetBookingContext owns all clearing. Every write is gated on the booking
  // still being the active one, so a slow response can't repopulate the form for a booking the
  // operator has already navigated away from.
  const fetchBookingDetails = useCallback(async (id: string) => {
    setFetchingBooking(true)

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${id}`, { credentials: 'include' })
      if (!response.ok) throw new Error('Failed to fetch booking details')

      const details: BookingDetails = await response.json()
      if (activeBookingIdRef.current !== id) return

      setBookingDetails(details)

      setCurrency(details.currency ?? 'USD')
      setSalesPrice((details.value ?? 0).toString())

      if (details.payment?.totalPaid) {
        setTotalPaidAmount(details.payment.totalPaid.toString())
      }

      // Pax breakdown: assume all non-infants are adults until user corrects
      const infants = parseInt(details.infantsTotal ?? '0') || 0
      const nonInfants = (details.travellersTotal ?? 0) - infants
      setAdultsCount(nonInfants > 0 ? nonInfants.toString() : '')
      setChildrenCount('0')

      // Net Fare Adult per pax: derive from PipeDrive netFareCost, fall back to TTV as conservative estimate
      if (details.netFareCost != null && nonInfants > 0) {
        setNetFareAdult((details.netFareCost / nonInfants).toFixed(2))
        setNetFareTtvEstimate(false)
      } else if (nonInfants > 0 && details.value > 0) {
        setNetFareAdult((details.value / nonInfants).toFixed(2))
        setNetFareTtvEstimate(true)
      } else {
        setNetFareAdult('')
        setNetFareTtvEstimate(false)
      }
      setNetFareChild('0')

      // Pre-fill from PipeDrive only when the recorded reason is one the dropdown can actually
      // show — anything else (including 'Unknown') leaves the operator to pick.
      const pdReason = details.cancellationAndRefund?.cancellationReason
      if (pdReason && CANCELLATION_REASONS.some(r => r === pdReason)) {
        setCancellationReason(pdReason)
      }
    } catch (error) {
      console.error('Error fetching booking details:', error)
      if (activeBookingIdRef.current !== id) return
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch booking details')
      setBookingDetails(null)
    } finally {
      if (activeBookingIdRef.current === id) setFetchingBooking(false)
    }
  }, [])

  // Looked up beside the quote, never instead of it: a code with no registered waiver is still sent to
  // the GDS, it just leaves the score alone.
  const lookUpRegisteredWaiver = useCallback(async (id: string, code: string | undefined) => {
    setRegisteredWaiver(null)
    setNoCodeshareConfirmed(false)
    setWaiverLookupMessage('')
    if (!code) return
    setWaiverLookupLoading(true)
    try {
      const found = await lookupWaiver(code)
      if (activeBookingIdRef.current === id) setRegisteredWaiver(found)
    } catch {
      if (activeBookingIdRef.current === id) {
        setWaiverLookupMessage(`No registered waiver has the code ${code}, so the confidence score is unchanged.`)
      }
    } finally {
      if (activeBookingIdRef.current === id) setWaiverLookupLoading(false)
    }
  }, [])

  const fetchRefundQuote = useCallback(
    async (id: string, waiverCode?: string) => {
      setRefundQuoteLoading(true)
      setRefundQuoteError('')
      setRefundQuoteData(null)
      lookUpRegisteredWaiver(id, waiverCode)

      try {
        const data = await retrieveRefundQuote(id, { waiverCode })
        if (activeBookingIdRef.current !== id) return

        setFareRulesLoading(true)
        // Tickets a change replaced are not refundable documents, so they are neither listed nor selected.
        setRefundQuoteData(withoutExchangedTickets(withConjunctionTicketKeys(data)))
        setSupplierEmailOverride(data.supplierEmail ?? '')
        setSupplierEmailDefault(data.supplierEmail ?? '')
        setCustomerEmailOverride(data.customerEmail ?? '')

        // Fetch fare rules in the background and merge when ready — same merge the fixture
        // tooling uses, so captured fixtures are byte-equivalent to what this UI computes from.
        const fareRulesAbort = new AbortController()
        const fareRulesTimer = setTimeout(() => fareRulesAbort.abort(), FARE_RULES_TIMEOUT_MS)
        fetch(`${config.apiBaseUrl}/bookings/${id}/fare-rules`, {
          credentials: 'include',
          cache: 'no-store',
          signal: fareRulesAbort.signal,
        })
          .then(r => (r.ok ? r.json() : Promise.reject()))
          .then(fareData => {
            if (activeBookingIdRef.current !== id) return
            setRefundQuoteData(prev => (prev ? mergeRefundQuotePayload(prev, fareData) : prev))
            setFareRulesLoading(false)
          })
          .catch(() => {
            if (activeBookingIdRef.current !== id) return
            setRefundQuoteData(prev => (prev ? { ...prev, fareRulesError: true } : prev))
            setFareRulesLoading(false)
          })
          .finally(() => clearTimeout(fareRulesTimer))
      } catch (error) {
        if (activeBookingIdRef.current !== id) return
        setRefundQuoteError(error instanceof Error ? error.message : 'Failed to fetch refund quote from GDS')
      } finally {
        if (activeBookingIdRef.current === id) setRefundQuoteLoading(false)
      }
    },
    [lookUpRegisteredWaiver],
  )

  const checkWorkflowStatus = useCallback(async (id: string) => {
    setWorkflowStepLoading(true)
    try {
      const resp = await fetch(`${config.apiBaseUrl}/bookings/${id}/cancel-execution/status`, {
        credentials: 'include',
      })
      if (activeBookingIdRef.current !== id) return
      const data = await resp.json().catch(() => ({}))
      if (activeBookingIdRef.current !== id) return
      // A rejected id answers 400 with a reason worth reading — reporting it as 'not-started' is
      // how a PNR in the box came to look like a booking that was never cancelled.
      setWorkflowStatusError(resp.ok ? '' : data.error ?? `Request failed (${resp.status})`)
      setWorkflowStep(resp.ok ? data.step ?? 'not-started' : null)
      setRefundStatus(resp.ok ? data.refundStatus ?? null : null)
    } finally {
      if (activeBookingIdRef.current === id) setWorkflowStepLoading(false)
    }
  }, [])

  // A cancellation still in flight owns the screen: a booking carries one, so the quote and the
  // Process Cancellation form describe something that cannot happen while one is running. A
  // finished one doesn't — 'completed' and 'not-expected' leave nothing to act on, and hiding the
  // quote there strands the operator on a page with two status chips and no way to look anything
  // up. Note the step alone reads 'not-started' once Restate purges a finished workflow, so the
  // refund status is what distinguishes finished from never-ran.
  const cancellationInFlight =
    refundStatus === 'monitoring' ||
    refundStatus === 'requires-review' ||
    (workflowStep !== null && workflowStep !== 'not-started' && workflowStep !== 'completed')
  const showQuoteUi = !cancellationInFlight

  const recheckRefundWithSabre = useCallback(async () => {
    setRecheckRefundLoading(true)
    setRecheckRefundError('')
    setRecheckRefundResult(null)
    try {
      const data = await postCancelExecution(bookingId.trim(), 'recheck-refund')
      const result = data as { refunded: boolean; detail: string }
      setRecheckRefundResult(result)
      if (result.refunded) setRefundStatus('completed')
    } catch (err) {
      setRecheckRefundError(messageFrom(err))
    } finally {
      setRecheckRefundLoading(false)
    }
  }, [bookingId])

  const confirmRefundReceived = useCallback(async () => {
    setConfirmRefundLoading(true)
    setConfirmRefundError('')
    try {
      await postCancelExecution(bookingId.trim(), 'confirm-refund')
      setConfirmRefundSuccess(true)
    } catch (err) {
      setConfirmRefundError(messageFrom(err))
    } finally {
      setConfirmRefundLoading(false)
    }
  }, [bookingId])

  const markRefundComplete = useCallback(async () => {
    setForceCompleteRefundLoading(true)
    setForceCompleteRefundError('')
    try {
      await postCancelExecution(bookingId.trim(), 'force-complete-refund')
      setForceCompleteRefundSuccess(true)
      setRefundStatus('completed')
    } catch (err) {
      setForceCompleteRefundError(messageFrom(err))
    } finally {
      setForceCompleteRefundLoading(false)
    }
  }, [bookingId])

  const forceCloseRefund = useCallback(async () => {
    setCloseRefundLoading(true)
    setCloseRefundError('')
    try {
      const outcome = (await postCancelExecution(bookingId.trim(), 'force-close')) as {
        action: string
        reason: string
      }
      // The action decides whether a refund is left alone, so it can answer "skipped" — saying
      // "closed" here regardless would report a refund as written off when it wasn't touched.
      if (outcome.action !== 'closed') {
        setCloseRefundError(outcome.reason)
        return
      }
      setCloseRefundSuccess(true)
      setRefundStatus('not-expected')
    } catch (err) {
      setCloseRefundError(messageFrom(err))
    } finally {
      setCloseRefundLoading(false)
    }
  }, [bookingId])

  // AI Assisted Quote — sends the already-loaded fare-rule text to a live LLM extraction, merges
  // the structured result back into refundQuoteData (auto-recomputing the quote below), and
  // surfaces any hasUnverifiablePenaltyClause flags for the operator to review.
  const runAiAssistedQuote = useCallback(
    async (id: string) => {
      if (!refundQuoteData?.fareRules?.length) return

      setAiQuoteLoading(true)
      setAiQuoteError('')
      setAiQuoteFailures([])
      setAiQuoteApplied(false)

      try {
        const response = await fetch(`${config.apiBaseUrl}/bookings/${id}/fare-rules/interpret`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ fareRules: refundQuoteData.fareRules }),
        })
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}))
          throw new Error(errData.error || 'Failed to interpret fare rules')
        }

        const result = await response.json()
        if (activeBookingIdRef.current !== id) return

        setRefundQuoteData(prev => (prev ? { ...prev, structuredFareRules: result.structuredFareRules } : prev))
        setAiQuoteFailures(result.failures ?? [])
        setAiQuoteApplied(true)
        setFareRulesExpanded(true)
      } catch (error) {
        if (activeBookingIdRef.current !== id) return
        setAiQuoteError(error instanceof Error ? error.message : 'Failed to interpret fare rules')
      } finally {
        if (activeBookingIdRef.current === id) setAiQuoteLoading(false)
      }
    },
    [refundQuoteData],
  )

  // Select all tickets when GDS data loads
  useEffect(() => {
    if (!refundQuoteData?.tickets?.length) return
    setSelectedTicketNumbers(new Set(refundQuoteData.tickets.map(t => t.number)))
  }, [refundQuoteData])

  // When refund quote loads, apply void window computed by the API.
  useEffect(() => {
    if (!refundQuoteData) {
      setIsVoidWindow(false)
      setVoidWindowExpired(false)
      return
    }

    const deadline = refundQuoteData.voidWindowDeadline
    setVoidWindowExpired(false)

    if (!deadline) {
      setIsVoidWindow(false)
      setVoidIssuedLabel('')
      setVoidDeadlineLabel('')
      return
    }

    // Banner only — the calculation pipeline derives void-window behaviour itself (deductions
    // and fees zeroed inside computeRefundQuoteStatement), no form mutation needed.
    setIsVoidWindow(true)

    const fmt = (dt: Date) =>
      new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(dt)
    setVoidIssuedLabel(refundQuoteData.bookingCreatedAt ? fmt(new Date(refundQuoteData.bookingCreatedAt)) : '')
    setVoidDeadlineLabel(fmt(new Date(deadline)))

    const msUntilDeadline = new Date(deadline).getTime() - Date.now()
    if (msUntilDeadline <= 0) {
      setVoidWindowExpired(true)
      return
    }

    const timer = setTimeout(() => setVoidWindowExpired(true), msUntilDeadline)
    return () => clearTimeout(timer)
  }, [refundQuoteData])

  useEffect(() => {
    if (isAdminFeeOverridden) return
    setAdminFee(refundQuoteData?.refundAdminFee ?? 0)
  }, [refundQuoteData?.refundAdminFee])

  useEffect(() => {
    getLiveScoring()
      .then(live => setScoring(live.settings))
      .catch(() => setScoring(undefined))
    // Without the table every carrier keeps the YQ/YR floor, the same as before it existed.
    getLiveCarrierSurcharges()
      .then(live => setCarrierSurcharges(live.carriers))
      .catch(() => setCarrierSurcharges(undefined))
  }, [])

  // Evaluated before the quote: a waiver with a fee per ticket is one of the calculator's inputs.
  const waiverEvaluation = useMemo(() => {
    if (!registeredWaiver || !bookingDetails || !refundQuoteData) return null
    return evaluateWaiver(registeredWaiver, {
      carrier: refundQuoteData.carrier ?? refundQuoteData.fareRules?.[0]?.airline,
      ticketingPcc: refundQuoteData.ticketingPcc,
      currency,
      bookedAt: refundQuoteData.bookingCreatedAt,
      ticketedAt: bookingDetails.ticketIssuedDateTime,
      departureDate: bookingDetails.departureDate,
      now: new Date(),
      agentConfirmedNoCodeshare: noCodeshareConfirmed,
    })
  }, [registeredWaiver, refundQuoteData, currency, bookingDetails, noCodeshareConfirmed])
  const waiverFee = useMemo(
    () => waiverFeeFor(registeredWaiver, waiverEvaluation),
    [registeredWaiver, waiverEvaluation],
  )

  // THE calculation: one call into the two-ledger statement pipeline (SPEC.md). Form fields feed
  // in as operator overrides; everything else is derived inside the package. `withOverrides: false`
  // gives the calculator's own figures, saved with the confidence level so overridden quotes can be
  // measured too.
  const computeQuote = useCallback(
    (withOverrides: boolean) => {
      if (!bookingDetails || refundQuoteData?.airlineControl) return null

      const rqdBase: RefundQuoteData = refundQuoteData ?? {}
      // Manual mode (no GDS tickets): thread the form's pax counts so booking-level resolution has them.
      const rqd = rqdBase.tickets?.length
        ? rqdBase
        : {
            ...rqdBase,
            paxAdtCount: parseInt(adultsCount) || rqdBase.paxAdtCount || 0,
            paxChdCount: parseInt(childrenCount) || rqdBase.paxChdCount || 0,
          }

      return computeRefundQuoteStatement({
        refundQuoteData: rqd,
        booking: {
          ...bookingDetails,
          value: parseFloat(salesPrice) || bookingDetails.value || 0,
          payment: {
            totalPaid: parseFloat(totalPaidAmount) || 0,
            discountAmount: bookingDetails.payment?.discountAmount ?? 0,
            deposit: bookingDetails.payment?.deposit ?? 0,
            pastDue: bookingDetails.payment?.pastDue ?? 0,
            planTotal: bookingDetails.payment?.planTotal,
          },
        },
        selectedTicketNumbers,
        currency,
        scoring,
        carrierSurcharges,
        waiverFee,
        overrides: withOverrides
          ? {
              grossSupplierRefund: isSupplierCashRefundOverridden ? parseFloat(supplierCashRefund) || 0 : undefined,
              airlineDeductions:
                airlineCancellationFeeOverride !== '' ? parseFloat(airlineCancellationFeeOverride) || 0 : undefined,
              pltFee: isPltFeeOverridden ? parseFloat(pltFeeOverride) || 0 : undefined,
              adminFee: isAdminFeeOverridden ? adminFee : undefined,
              netFarePerAdult:
                isNetFareManuallyEdited || rqdBase.netFarePerAdt === undefined
                  ? parseFloat(netFareAdult) || undefined
                  : undefined,
              netFarePerChild: isNetFareManuallyEdited ? parseFloat(netFareChild) || undefined : undefined,
            }
          : {
              // With no GDS fare the form's PD/TTV placeholder is the only fare there is, not an override.
              netFarePerAdult:
                !isNetFareManuallyEdited && rqdBase.netFarePerAdt === undefined
                  ? parseFloat(netFareAdult) || undefined
                  : undefined,
            },
      })
    },
    [
      bookingDetails,
      refundQuoteData,
      selectedTicketNumbers,
      currency,
      salesPrice,
      totalPaidAmount,
      isSupplierCashRefundOverridden,
      supplierCashRefund,
      airlineCancellationFeeOverride,
      isPltFeeOverridden,
      pltFeeOverride,
      isAdminFeeOverridden,
      adminFee,
      isNetFareManuallyEdited,
      netFareAdult,
      netFareChild,
      adultsCount,
      childrenCount,
      scoring,
      carrierSurcharges,
      waiverFee,
    ],
  )
  const quote = useMemo(() => computeQuote(true), [computeQuote])
  const calculatedQuote = useMemo(() => computeQuote(false), [computeQuote])

  // Upgrade the net-fare form fields from the PD/TTV placeholder to the GDS figure when it loads —
  // never overwriting a manual edit.
  useEffect(() => {
    if (isNetFareManuallyEdited || refundQuoteData?.netFarePerAdt === undefined) return
    const fx = refundQuoteData.fareToBookingCurrencyRate ?? 1
    const adtFare = refundQuoteData.netFarePerAdt * fx
    const chdCount = refundQuoteData.paxChdCount ?? 0
    const chdFare =
      chdCount > 0 ? ((refundQuoteData.netFarePerChd ?? 0) > 0 ? refundQuoteData.netFarePerChd! * fx : adtFare) : 0
    setNetFareAdult(adtFare.toFixed(2))
    setNetFareChild(chdFare.toFixed(2))
    setNetFareTtvEstimate(false)
  }, [refundQuoteData, isNetFareManuallyEdited])

  const statement = quote?.statement ?? null
  const deductions = quote?.deductions ?? null
  // PLT's own calibration of how much to trust this quote (see confidence-score.ts) — independent
  // of anything the GDS itself reports. Not shown until a quote actually exists.
  const confidence =
    quote && registeredWaiver && waiverEvaluation
      ? applyWaiverToConfidence(quote.confidence, registeredWaiver, waiverEvaluation)
      : quote?.confidence ?? null
  // A waiver waiting on the agent's codeshare check would save the wrong level, so the save waits for it.
  const waiverAwaitingAgent = waiverEvaluation?.checks.some(check => check.state === 'agent-to-confirm') ?? false
  const confidenceStyle: Record<string, { badge: string; dot: string }> = {
    high: { badge: 'bg-green-50 text-green-700 border-green-300', dot: 'bg-green-500' },
    medium: { badge: 'bg-amber-50 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
    low: { badge: 'bg-orange-50 text-orange-700 border-orange-300', dot: 'bg-orange-500' },
    none: { badge: 'bg-red-50 text-red-700 border-red-300', dot: 'bg-red-500' },
  }

  // Saves the level this screen computed onto the quote record check-tickets-handlers.ts already wrote.
  // It goes out once the quote has settled. An agent who processes the cancellation, or leaves the
  // quote open, before the fare rules arrive would otherwise leave the record without a level, so a
  // provisional copy also goes out after a delay and when the cancellation is processed. The settled
  // value replaces it if the agent is still here, and an override of the level stops all of them.
  const confidenceLog = useRef<ConfidenceReportLog>(emptyConfidenceReportLog())
  const latestQuoteState = useRef({
    requestId: refundQuoteData?.cancellationRequestId,
    bookingId,
    confidence,
    quote,
    calculatedQuote,
  })
  latestQuoteState.current = {
    requestId: refundQuoteData?.cancellationRequestId,
    bookingId,
    confidence,
    quote,
    calculatedQuote,
  }

  const sendConfidence = useCallback((reason: ConfidenceReportReason) => {
    const {
      requestId,
      bookingId: id,
      confidence: level,
      quote: current,
      calculatedQuote: calculated,
    } = latestQuoteState.current
    if (!requestId || !level || !shouldReportConfidence(confidenceLog.current, requestId, reason)) return
    confidenceLog.current = recordConfidenceReport(confidenceLog.current, requestId, reason)
    fetch(`${config.apiBaseUrl}/bookings/${id}/refund-quote/confidence`, {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        cancellationRequestId: requestId,
        confidenceLevel: level.level,
        confidenceScore: level.score,
        confidenceSource: level.source,
        confidenceWarnings: warningCodes(current?.warnings ?? []),
        confidenceReportKind: reason,
        calculatedFigures: calculated
          ? {
              supplierRefund: calculated.statement.expectedSupplierRefund,
              refundToPax: calculated.statement.refundToPax,
              deductions: calculated.deductions.total,
            }
          : undefined,
      }),
    }).catch(() => {})
  }, [])

  // The first quote after a load is incomplete twice over: the select-all effect runs one render
  // later, and fare rules arrive from a second request. Scoring either gap gives `none`.
  const quoteSettled = isQuoteSettled({
    quoteLoading: refundQuoteLoading || waiverLookupLoading || waiverAwaitingAgent,
    fareRulesLoading,
    source: deductions?.source,
    ticketCount: refundQuoteData?.tickets?.length ?? 0,
    selectedTicketCount: selectedTicketNumbers.size,
  })
  // The figures, the badge and every action that uses them wait for the fare rules too: they can cap a
  // quoted penalty to the filed fee seconds after the quote first renders.
  const awaitingFareRules = isAwaitingFareRules({ fareRulesLoading, source: deductions?.source })
  useEffect(() => {
    if (quoteSettled) sendConfidence('settled')
  }, [refundQuoteData?.cancellationRequestId, confidence, quoteSettled, sendConfidence])
  useEffect(() => {
    if (!refundQuoteData?.cancellationRequestId) return
    const timer = setTimeout(() => sendConfidence('fallback'), CONFIDENCE_FALLBACK_MS)
    return () => clearTimeout(timer)
  }, [refundQuoteData?.cancellationRequestId, sendConfidence])

  // Penalty and admin fee are both charges the airline imposes for cancelling; non-refundable
  // taxes are a different thing entirely (surcharges that just don't come back) and are shown
  // as their own line rather than folded into "Airline Penalties".
  const airlinePenaltyAmount = (deductions?.penalty ?? 0) + (deductions?.adminFee ?? 0)
  const requiresManualDeductions = quote?.flags.requiresManualDeductions ?? false
  // No ticket to select: never issued, or an LCC booking, which has none even once paid.
  const ticketless = Boolean(refundQuoteData?.noTicketsIssued || refundQuoteData?.lccBooking)
  // Shared by the request and the confirm/submitted texts, so what Ops read matches what is sent.
  // void path: airline was never charged — no RF/XL supplier fields needed.
  // noTicketsIssued outside void window (e.g. XLQR cancellations): use 'full'
  // so rfSupplierTotalCashRefund, xlLayByTerminationFee etc. are written to PD.
  const selectedTicketCount = (refundQuoteData?.tickets ?? []).filter(t => selectedTicketNumbers.has(t.number)).length
  const automatedCancellationType = isVoidWindow
    ? 'void'
    : refundQuoteData?.noTicketsIssued
      ? 'full'
      : selectedTicketCount < (refundQuoteData?.tickets?.length ?? 0)
        ? 'partial'
        : 'full'
  const ticketPenaltyMap: Record<string, number> = Object.fromEntries(
    (deductions?.perTicket ?? []).map(d => [d.ticketNumber, d.penalty]),
  )
  // Refundable taxes across selected automated tickets — stats-only figure in the payload.
  const refundableTaxAmount = (refundQuoteData?.tickets ?? [])
    .filter(t => selectedTicketNumbers.has(t.number) && t.isRefundable && t.refundTotals)
    .reduce((sum, t) => sum + (parseFloat(t.refundTotals!.taxes) || 0), 0)
  // The gross figure the "Airline Refund to PLT" field shows when not overridden.
  const displayGrossRefund = isSupplierCashRefundOverridden
    ? supplierCashRefund
    : requiresManualDeductions
      ? supplierCashRefund
      : (quote?.grossAirlineRefund ?? 0).toFixed(2)

  // Debounced booking fetch. The reset runs synchronously on every ID change, so the form blanks
  // at once rather than showing the previous booking's figures for the debounce plus round-trip.
  useEffect(() => {
    const id = bookingId.trim()
    activeBookingIdRef.current = id
    resetBookingContext()

    const timer = setTimeout(() => {
      if (id) {
        fetchBookingDetails(id)
        checkWorkflowStatus(id)
      }
    }, 800)

    return () => clearTimeout(timer)
  }, [bookingId, fetchBookingDetails, checkWorkflowStatus, resetBookingContext])

  // Foreign-penalty warnings: GDS ticket penalties filed in another currency, with/without a
  // usable conversion rate (mirrors the old ops-quote derivation).
  const penaltyFxRate = refundQuoteData?.penaltyToBookingCurrencyRate ?? refundQuoteData?.fareToBookingCurrencyRate
  const anyForeignPenalty = (refundQuoteData?.tickets ?? []).some(
    t =>
      selectedTicketNumbers.has(t.number) &&
      t.isRefundable === true &&
      !t.refundTotals &&
      hasForeignCurrencyPenalty(t, currency),
  )
  const hasForeignPenaltyCurrencyMismatch = !penaltyFxRate && anyForeignPenalty
  const hasForeignCurrencyAutoConverted = !!penaltyFxRate && anyForeignPenalty
  const isAllSelectedNonRefundable = quote?.flags.isAllSelectedNonRefundable ?? false
  const isTaxesOnlyRefund = quote?.flags.isTaxesOnlyRefund ?? false
  const hasNonAutomatedRefundableTickets = (refundQuoteData?.tickets ?? []).some(
    t =>
      selectedTicketNumbers.has(t.number) &&
      t.isRefundable === true &&
      !t.isAutomatedRefundsEligible &&
      !t.refundTotals,
  )
  const isAtcoPenaltyFallback = quote?.flags.isEstimate ?? false

  // PD vs GDS discrepancy checks — PD is a cross-check here, not a value source. GDS/BMAPI ticket
  // data drives the actual calculation; if PD's deal-level figures disagree, that's a signal to
  // investigate (e.g. a passenger dropped from the booking), not something to silently resolve.
  const gdsTotalPax =
    refundQuoteData?.paxAdtCount !== undefined
      ? (refundQuoteData.paxAdtCount ?? 0) + (refundQuoteData.paxChdCount ?? 0) + (refundQuoteData.paxInfCount ?? 0)
      : undefined
  const hasPaxCountMismatch =
    gdsTotalPax !== undefined &&
    bookingDetails?.travellersTotal !== undefined &&
    gdsTotalPax !== bookingDetails.travellersTotal

  const pdSupplierCashRefund = bookingDetails?.cancellationAndRefund?.rfSupplierTotalCashRefund
  const computedSupplierCashRefund = statement?.expectedSupplierRefund
  const hasSupplierCashRefundMismatch =
    pdSupplierCashRefund != null &&
    computedSupplierCashRefund != null &&
    !requiresManualDeductions &&
    Math.abs(pdSupplierCashRefund - computedSupplierCashRefund) > 0.01

  // Browsers hold back timers in background tabs and while a laptop sleeps, so the expiry timer can
  // fire late; a click checks the clock itself.
  const voidWindowClosedNow = () => {
    const deadline = refundQuoteData?.voidWindowDeadline
    if (!deadline || Date.now() < new Date(deadline).getTime()) return false

    setVoidWindowExpired(true)
    return true
  }

  const copyQuoteToClipboard = async () => {
    if (!quote || !statement || !deductions || voidWindowClosedNow()) return
    const money = (n: number) => `${currency} ${n.toFixed(2)}`
    // Headline first — the figures an agent reads to the customer. Workings below, clearly marked
    // internal so they never get pasted into a customer-facing message by accident.
    const quoteText = `CANCELLATION REFUND QUOTE

Booking ID: ${bookingId}
Passengers: ${adultsCount} adult(s), ${childrenCount} child(ren)

Reason for Cancellation:
${cancellationReason}

Customer Paid: ${money(statement.paxLedger.totalPaid)}
Airline Penalties: -${money(deductions.penalty + deductions.adminFee)}${deductions.nonRefundableTaxes > 0 ? `\nNon-Refundable Amount: -${money(deductions.nonRefundableTaxes)}` : ''}
Our Fees: -${money(quote.pltFee)}
${statement.isShortfall ? 'CUSTOMER OWES (SHORTFALL)' : 'MONEY BACK TO CUSTOMER'}: ${money(statement.isShortfall ? Math.abs(statement.paxLedger.raw) : statement.refundToPax)}

--- BREAKDOWN (internal) ---
Plan Total / TTV: ${money(parseFloat(salesPrice) || 0)}
Airline penalties (${deductions.source.toLowerCase().replace(/_/g, ' ')}): penalty ${money(deductions.penalty)}, non-refundable taxes ${money(deductions.nonRefundableTaxes)}${deductions.adminFee > 0 ? `, airline admin fee ${money(deductions.adminFee)}` : ''}
Nett Paid to Supplier: ${money(quote.nettPaid.total)}
Gross Airline Refund: +${money(quote.grossAirlineRefund)}${quote.supplierFee > 0 ? `\nSupplier Fee (consolidator): -${money(quote.supplierFee)}` : ''}
Expected Supplier Refund (net cash in): ${money(statement.expectedSupplierRefund)}

Generated on: ${new Date().toLocaleString()}`

    try {
      await navigator.clipboard.writeText(quoteText)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const hasGdsData = !!refundQuoteData && !refundQuoteData.airlineControl && !refundQuoteData.accessRestricted
  const isAlreadyProcessed = refundQuoteData?.allCouponsProcessed === true
  const noRefundDataWarning =
    bookingDetails &&
    !fetchingBooking &&
    !refundQuoteData &&
    !netFareAdult &&
    (parseFloat(supplierCashRefund) || 0) === 0

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Refunds</span>
        <a
          href={config.routePrefix + '/refunds/surcharges'}
          className="ml-auto text-sm text-indigo-600 hover:text-indigo-800"
        >
          YQ/YR by carrier
        </a>
        <a
          href={config.routePrefix + '/refunds/scoring'}
          className="ml-4 text-sm text-indigo-600 hover:text-indigo-800"
        >
          Scoring settings
        </a>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Refunds &amp; Cancellations</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      {/* Booking ID */}
      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="bookingId"
            type="text"
            value={bookingId}
            onChange={e => setBookingId((e.target as HTMLInputElement).value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter Booking ID"
          />
          {fetchingBooking && (
            <div className="absolute right-3 top-2">
              <svg
                className="animate-spin h-5 w-5 text-indigo-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}
        </div>
        {errorMessage && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
        {bookingId && isFrontApp && <p className="mt-1 text-xs text-gray-500">Auto-detected from Front conversation</p>}
        {bookingDetails && <p className="mt-1 text-xs text-green-600">Booking details loaded successfully</p>}
        {fetchingBooking && <p className="mt-1 text-xs text-blue-600">Loading booking details...</p>}
      </div>

      <div className="space-y-4">
        {/* Cancellation workflow status — works for any in-flight workflow, not just one submitted this session */}
        {bookingId ? (
          <div className="pt-2 border-t border-gray-200 space-y-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={workflowStepLoading}
                onClick={() => checkWorkflowStatus(bookingId.trim())}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {workflowStepLoading ? 'Checking…' : 'Check workflow status'}
              </button>
              {workflowStep !== null && (
                <span className="text-xs text-gray-600">
                  Step: <span className="font-mono font-medium">{workflowStep}</span>
                </span>
              )}
              {refundStatus !== null && (
                <span className="text-xs text-gray-600">
                  Refund:{' '}
                  <span className={`font-mono font-medium ${refundStatus === 'requires-review' ? 'text-red-700' : ''}`}>
                    {refundStatus}
                  </span>
                </span>
              )}
            </div>
            {workflowStatusError && <p className="text-xs text-red-600">{workflowStatusError}</p>}
            {workflowStep === 'sabre-stopped' && (
              <div
                className="p-3 bg-red-50 border-l-4 border-red-500 rounded-md"
                role="alert"
              >
                <p className="text-sm font-semibold text-red-800">Sabre could not cancel this booking</p>
                <p className="text-sm text-red-700 mt-1">
                  {refundQuoteData && isJetstarLccBooking(refundQuoteData)
                    ? JETSTAR_AGENT_HUB_MESSAGE
                    : 'The cancellation stopped before Pipedrive, the payment plan or any email. Finish it by hand; the CCM ticket has the details.'}
                </p>
              </div>
            )}
            {/* Step reaches 'completed' unconditionally once monitoring exits, even when the
                refund itself was never confirmed — refund_status is the field that actually
                reflects whether the money came back, so gate this override on it directly
                rather than on workflowStep (which can no longer show the live-polling button). */}
            {refundStatus === 'requires-review' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
                <p className="text-xs text-amber-800">
                  This refund was escalated for manual review and the workflow has already finished polling — it will
                  not recheck Sabre on its own. Recheck below to ask Sabre right now, or mark it complete directly if
                  you've already confirmed the refund elsewhere.
                </p>
                {recheckRefundResult && (
                  <p
                    className={`text-xs font-medium ${recheckRefundResult.refunded ? 'text-green-700' : 'text-gray-600'}`}
                  >
                    {recheckRefundResult.refunded
                      ? 'Sabre confirms refunded — marked complete.'
                      : `Not yet, per Sabre: ${recheckRefundResult.detail}`}
                  </p>
                )}
                {recheckRefundError && <p className="text-xs text-red-600">{recheckRefundError}</p>}
                {forceCompleteRefundSuccess ? (
                  <p className="text-xs font-medium text-green-700">Refund marked complete.</p>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={recheckRefundLoading}
                      onClick={recheckRefundWithSabre}
                      className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded-md hover:bg-amber-100 disabled:opacity-50"
                    >
                      {recheckRefundLoading ? 'Checking Sabre…' : 'Recheck Sabre now'}
                    </button>
                    {forceCompleteRefundError && <p className="text-xs text-red-600">{forceCompleteRefundError}</p>}
                    <button
                      type="button"
                      disabled={forceCompleteRefundLoading}
                      onClick={markRefundComplete}
                      className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
                    >
                      {forceCompleteRefundLoading ? 'Marking…' : 'Refund confirmed in Sabre — mark complete'}
                    </button>
                  </div>
                )}
              </div>
            )}
            {refundStatus === 'monitoring' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md space-y-2">
                <p className="text-xs text-blue-800">
                  This refund is awaiting confirmation. Recheck Sabre now for its current status, confirm below if you
                  already know it's refunded, or close it if no refund is coming at all.
                </p>
                {recheckRefundResult && (
                  <p
                    className={`text-xs font-medium ${recheckRefundResult.refunded ? 'text-green-700' : 'text-gray-600'}`}
                  >
                    {recheckRefundResult.refunded
                      ? 'Sabre confirms refunded — marked complete.'
                      : `Not yet, per Sabre: ${recheckRefundResult.detail}`}
                  </p>
                )}
                {recheckRefundError && <p className="text-xs text-red-600">{recheckRefundError}</p>}
                <button
                  type="button"
                  disabled={recheckRefundLoading}
                  onClick={recheckRefundWithSabre}
                  className="px-3 py-1.5 text-xs font-medium text-blue-800 bg-white border border-blue-300 rounded-md hover:bg-blue-100 disabled:opacity-50"
                >
                  {recheckRefundLoading ? 'Checking Sabre…' : 'Recheck Sabre now'}
                </button>
                {confirmRefundSuccess ? (
                  <p className="text-xs font-medium text-green-700">
                    Refund confirmation submitted. Check workflow status to verify completion.
                  </p>
                ) : (
                  <>
                    {confirmRefundError && <p className="text-xs text-red-600">{confirmRefundError}</p>}
                    <button
                      type="button"
                      disabled={confirmRefundLoading}
                      onClick={confirmRefundReceived}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {confirmRefundLoading ? 'Confirming…' : 'Coupons refunded in Sabre — confirm now'}
                    </button>
                  </>
                )}
              </div>
            )}
            {refundStatus !== null && refundStatus !== 'completed' && refundStatus !== 'not-expected' && (
              <div className="p-3 bg-gray-50 border border-gray-300 rounded-md space-y-2">
                <p className="text-xs text-gray-700">
                  No refund is coming — a default or loss-recovery cancellation. Closing stops the poll if one is still
                  running, records the refund as not expected, and notes who closed it on the cancellation record.
                </p>
                {closeRefundSuccess ? (
                  <p className="text-xs font-medium text-green-700">Closed — recorded as no refund expected.</p>
                ) : (
                  <>
                    {closeRefundError && <p className="text-xs text-red-600">{closeRefundError}</p>}
                    <button
                      type="button"
                      disabled={closeRefundLoading}
                      onClick={forceCloseRefund}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                    >
                      {closeRefundLoading ? 'Closing…' : 'Force close — no refund expected'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : undefined}
        {/* Void window banner */}
        {showQuoteUi && refundQuoteData?.noTicketsIssued && !refundQuoteLoading && (
          <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-md space-y-1">
            <p className="text-sm font-semibold text-amber-800">No tickets issued — full refund applies</p>
            <p className="text-sm text-amber-700">
              The airline was never charged for this booking. Treat as a full refund of the amount paid by the customer.
            </p>
          </div>
        )}
        {showQuoteUi && refundQuoteData !== null && !refundQuoteLoading && isVoidWindow && (
          <div className="p-4 bg-teal-50 border-l-4 border-teal-500 rounded-md space-y-1">
            <p className="text-sm font-semibold text-teal-800">Void window — full refund applies</p>
            <p className="text-sm text-teal-700">A void cancels every ticket on the booking.</p>
            {voidWindowExpired && (
              <p className="text-sm font-semibold text-amber-700">
                The void window has closed. Retrieve the refund quote again before cancelling.
              </p>
            )}
            {voidIssuedLabel && <p className="text-sm text-teal-700">Booking created: {voidIssuedLabel}</p>}
            {voidDeadlineLabel && (
              <p className="text-sm text-teal-700">Free cancellation deadline: {voidDeadlineLabel}</p>
            )}
          </div>
        )}
        {showQuoteUi && refundQuoteData?.ndcCancelQuote && !refundQuoteLoading && (
          <NdcCancelQuotePanel quote={refundQuoteData.ndcCancelQuote} />
        )}

        {showQuoteUi && (
          <>
            {/* Waiver Code */}
            <div>
              <label
                htmlFor="waiverCode"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Waiver Code{' '}
                <span className="text-xs font-normal text-gray-500">
                  (optional — applied to GDS quote and cancellation)
                </span>
              </label>
              <input
                id="waiverCode"
                type="text"
                value={waiverCode}
                onChange={e => setWaiverCode((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                placeholder="optional"
              />
              {waiverLookupLoading && <p className="mt-1 text-xs text-gray-500">Checking registered waivers…</p>}
              {waiverLookupMessage && <p className="mt-1 text-xs text-gray-500">{waiverLookupMessage}</p>}
              {registeredWaiver && waiverEvaluation && (
                <div
                  className={
                    'mt-2 p-3 rounded-md border text-sm space-y-2 ' +
                    (waiverEvaluation.applies ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300')
                  }
                >
                  <p className="font-medium text-gray-800">
                    Registered waiver {registeredWaiver.code}:{' '}
                    {waiverEvaluation.applies
                      ? registeredWaiver.airlineFeePerTicket == null
                        ? `every condition is met, so the quote scores at least ${registeredWaiver.score}.`
                        : `every condition is met, so the airline fee is ${currency} ${registeredWaiver.airlineFeePerTicket.toFixed(2)} per ticket and the quote scores at least ${registeredWaiver.score}.`
                      : 'the quote is unchanged until every condition is met.'}
                  </p>
                  <ul className="space-y-1">
                    {waiverEvaluation.checks.map(check => (
                      <li
                        key={check.condition}
                        className="flex items-start gap-2"
                      >
                        <span
                          className={
                            'inline-block mt-0.5 px-1.5 rounded text-xs font-medium ' + WAIVER_CHECK_STYLE[check.state]
                          }
                        >
                          {WAIVER_CHECK_LABEL[check.state]}
                        </span>
                        <span className="text-gray-700">
                          {check.condition} <span className="text-gray-500">({check.detail})</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {!registeredWaiver.codeshareAllowed && (
                    <label className="flex items-center gap-2 text-gray-800">
                      <input
                        type="checkbox"
                        checked={noCodeshareConfirmed}
                        onChange={e => setNoCodeshareConfirmed((e.target as HTMLInputElement).checked)}
                      />
                      I checked the itinerary and there are no codeshare segments
                    </label>
                  )}
                  {registeredWaiver.airlineFeePerTicket == null && (
                    <p className="text-xs text-gray-500">
                      The waiver changes the confidence score only, not the refund.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* GDS Refund Quote */}
            <div>
              <button
                type="button"
                onClick={() => fetchRefundQuote(bookingId.trim(), waiverCode.trim() || undefined)}
                disabled={!bookingId.trim() || refundQuoteLoading || fareRulesLoading}
                className="w-full px-4 py-2 bg-teal-600 text-white font-medium rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {refundQuoteLoading
                  ? 'Pulling from GDS...'
                  : fareRulesLoading
                    ? 'Loading fare rules...'
                    : 'Retrieve Refund Quote'}
              </button>
              {refundQuoteLoading && <QuoteLoadingPanel />}
              {refundQuoteError && <p className="mt-1 text-sm text-red-600">{refundQuoteError}</p>}
            </div>

            {/* AI Assisted Quote */}
            <div>
              <button
                type="button"
                onClick={() => runAiAssistedQuote(bookingId.trim())}
                disabled={!bookingId.trim() || !refundQuoteData?.fareRules?.length || aiQuoteLoading}
                className="w-full px-4 py-2 bg-purple-600 text-white font-medium rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {aiQuoteLoading ? 'Interpreting fare rules...' : 'AI Assisted Quote'}
              </button>
              {aiQuoteError && <p className="mt-1 text-sm text-red-600">{aiQuoteError}</p>}
              {aiQuoteApplied && !aiQuoteError && (
                <p className="mt-1 text-sm text-purple-700">
                  AI-assisted values applied ✓ — see GDS Fare Rules below for the extracted summary.
                </p>
              )}
              {aiQuoteFailures.length > 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  {aiQuoteFailures.length} fare component{aiQuoteFailures.length > 1 ? 's' : ''} could not be
                  interpreted ({aiQuoteFailures.map(f => f.fareBasis).join(', ')}) — remaining components still applied.
                </p>
              )}
            </div>
          </>
        )}

        {showQuoteUi && refundQuoteData?.structuredFareRules?.some(r => r.hasUnverifiablePenaltyClause) && (
          <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-md space-y-2">
            <p className="text-sm font-semibold text-amber-800">
              AI flagged a fare condition that can't be verified from the filed text alone
            </p>
            {refundQuoteData.structuredFareRules
              .filter(r => r.hasUnverifiablePenaltyClause)
              .map((r, i) => (
                <p
                  key={i}
                  className="text-sm text-amber-700"
                >
                  <span className="font-medium">
                    {r.fareBasis} ({r.originDestination}):
                  </span>{' '}
                  {r.notes || 'See GDS Fare Rules below for the raw clause.'}
                </p>
              ))}
            <p className="text-xs text-amber-600">
              The penalty/tax figures above may not be the final word — verify against the raw fare rule text or confirm
              with the consolidator before processing.
            </p>
          </div>
        )}

        {showQuoteUi && refundQuoteData?.lccBooking && (
          <div
            className="p-4 bg-red-50 border-l-4 border-red-500 rounded-md"
            role="alert"
          >
            <p className="text-sm font-semibold text-red-800">LCC booking — this will not process a cancel</p>
            {isJetstarLccBooking(refundQuoteData) ? (
              <JetstarCancellationRules />
            ) : (
              <p className="text-sm text-red-700 mt-1">
                Please cancel it outside this tool, in the airline&rsquo;s own portal. Sabre cannot void or cancel it,
                so the penalties below are for quoting only.
              </p>
            )}
          </div>
        )}

        {showQuoteUi && refundQuoteData?.airlineControl && (
          <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
            <p className="text-sm font-semibold text-amber-800">Flight is under airline control</p>
            <p className="text-sm text-amber-700 mt-1">Cancellations must be done directly with the airline.</p>
          </div>
        )}

        {showQuoteUi && refundQuoteData?.accessRestricted && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
            <p className="text-sm font-semibold text-red-800">Booking access restricted — cannot quote</p>
            <p className="text-sm text-red-700 mt-1">
              The GDS returned this booking as secured/restricted (or could not locate it), so its tickets and fare
              rules can&rsquo;t be read via this PCC. This is <strong>not</strong> an unticketed booking &mdash; do not
              process a refund. Verify the PCC / credentials and Travel Journal Record access with the account manager
              (the PNR may be secured to a different ticketing PCC), then retry.
            </p>
          </div>
        )}

        {showQuoteUi && refundQuoteData?.refundabilityUnavailable && (
          <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded-md">
            <p className="text-sm font-semibold text-red-800">Unable to retrieve refundability data from GDS</p>
            <p className="text-sm text-red-700 mt-1">
              {refundQuoteData.tickets?.length
                ? 'Ticket refundability could not be checked (PCC does not support automated refunds). Verify fare rules manually and enter the Supplier Cash Refund using the override field below.'
                : 'Use the manual calculator (V3) to assess the refund amount.'}
            </p>
          </div>
        )}

        {showQuoteUi && refundQuoteData?.partialData && !refundQuoteData.airlineControl && (
          <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
            <p className="text-sm text-yellow-800">
              Partial / Incomplete data — some ticket conditions may be missing or inaccurate. Tickets marked{' '}
              <strong>Unknown — check failed</strong> could not be assessed by Sabre (e.g. airline does not support
              automated refunds). Manually verify fare rules and use the Supplier Cash Refund override to calculate the
              correct amount.
            </p>
          </div>
        )}

        {showQuoteUi && isAlreadyProcessed && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-md space-y-2">
            <p className="text-sm font-semibold text-red-800">Booking already processed — no active coupons</p>
            <p className="text-sm text-red-700">
              All ticket coupons are in a final state (refunded, exchanged, or flown). The calculation form is locked.
            </p>
            <button
              type="button"
              onClick={() => setForceWorkflowOverride(v => !v)}
              className={`text-xs px-2 py-1 rounded border font-medium ${forceWorkflowOverride ? 'bg-red-200 text-red-900 border-red-400' : 'bg-white text-red-700 border-red-300 hover:bg-red-100'}`}
            >
              {forceWorkflowOverride ? 'Override active — click to cancel' : 'Force-trigger workflow anyway (override)'}
            </button>
          </div>
        )}

        {/* GDS accordion */}
        {showQuoteUi && hasGdsData && (
          <div className="border border-gray-200 rounded-md bg-gray-50 overflow-hidden">
            {/* GDS Refund Conditions */}
            <button
              type="button"
              onClick={() => setRefundConditionsExpanded(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
            >
              <span className="flex items-center gap-2">
                GDS Refund Conditions
                {bookingDetails?.departureDate && (
                  <span
                    className={`text-xs font-normal px-1.5 py-0.5 rounded ${new Date() >= new Date(bookingDetails.departureDate) ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}
                  >
                    {new Date() >= new Date(bookingDetails.departureDate) ? 'After departure' : 'Before departure'}
                  </span>
                )}
                <span className="text-xs font-normal text-gray-500">
                  {selectedTicketNumbers.size} of {refundQuoteData.tickets?.length ?? 0} ticket
                  {(refundQuoteData.tickets?.length ?? 0) !== 1 ? 's' : ''} selected
                </span>
              </span>
              <span className="text-gray-400">{refundConditionsExpanded ? '▲' : '▼'}</span>
            </button>

            {refundConditionsExpanded && (
              <div className="px-3 pb-3 space-y-4">
                {isVoidWindow && (
                  <p className="text-xs text-teal-700">
                    A void cancels every ticket on the booking, so all tickets stay selected. To cancel only some
                    passengers, wait until the void window closes and process a partial cancellation.
                  </p>
                )}
                {(refundQuoteData.tickets ?? []).map(ticket => {
                  const isSelected = selectedTicketNumbers.has(ticket.number)
                  const depDate = bookingDetails?.departureDate
                  const timing = depDate && new Date() >= new Date(depDate) ? 'AFTER_DEPARTURE' : 'BEFORE_DEPARTURE'
                  return (
                    <div
                      key={ticket.number}
                      className={`border rounded p-3 bg-white space-y-2 ${isSelected ? 'border-teal-300' : 'border-gray-200 opacity-60'}`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isVoidWindow}
                          onChange={e => {
                            const next = new Set(selectedTicketNumbers)
                            if ((e.target as HTMLInputElement).checked) next.add(ticket.number)
                            else next.delete(ticket.number)
                            setSelectedTicketNumbers(next)
                          }}
                          className={`h-4 w-4 text-teal-600 rounded border-gray-300 ${isVoidWindow ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        />
                        <p className="text-xs font-mono text-gray-500">
                          Ticket: {ticket.number}
                          {refundQuoteData.travelersByTicket?.[ticket.number] && (
                            <span className="ml-2 text-gray-700 not-italic font-sans">
                              — {refundQuoteData.travelersByTicket[ticket.number]}
                            </span>
                          )}
                        </p>
                      </div>

                      {refundQuoteData.ticketStatusByTicket?.[ticket.number] && (
                        <TicketStatusLine summary={refundQuoteData.ticketStatusByTicket[ticket.number]} />
                      )}

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <span className="text-gray-500">Refundable</span>
                        <span
                          className={`text-right font-medium ${ticket.isRefundable === true ? (parseFloat(ticket.refundTotals?.subtotal ?? '1') === 0 ? 'text-amber-600' : 'text-green-700') : ticket.isRefundable === false ? (isAtcoPenaltyFallback ? 'text-amber-600' : 'text-red-700') : 'text-amber-600'}`}
                        >
                          {ticket.isRefundable === true
                            ? parseFloat(ticket.refundTotals?.subtotal ?? '1') === 0
                              ? 'Taxes only'
                              : 'YES'
                            : ticket.isRefundable === false
                              ? isAtcoPenaltyFallback
                                ? 'NO — ATPCO est. applied'
                                : 'NO'
                              : 'Unknown — check failed'}
                        </span>
                        {ticket.isRefundable && (
                          <>
                            <span className="text-gray-500">Auto RF</span>
                            <span
                              className={`text-right ${ticket.isAutomatedRefundsEligible ? 'text-green-700 font-medium' : 'text-gray-700'}`}
                            >
                              {ticket.isAutomatedRefundsEligible ? 'YES' : 'NO'}
                            </span>
                          </>
                        )}
                      </div>

                      {ticket.isRefundable && ticket.refundPenalties && ticket.refundPenalties.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-600">Penalties</p>
                          {ticket.refundPenalties.map((p, i) => (
                            <div
                              key={i}
                              className="grid grid-cols-2 gap-x-4 text-sm"
                            >
                              <span className="text-gray-500 text-xs">
                                {p.applicability === 'BEFORE_DEPARTURE' ? 'Before dep' : 'After dep'}
                                {p.source ? ` (${p.source})` : ''}
                              </span>
                              <span className="text-gray-800 text-right">
                                {p.penalty ? `${p.penalty.amount} ${p.penalty.currencyCode}` : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {(() => {
                        const allTaxes = refundQuoteData.allTaxesByTicket?.[ticket.number]
                        const refundableCodes = new Set(ticket.refundTaxes?.map(t => t.taxCode) ?? [])
                        if (allTaxes && allTaxes.length > 0) {
                          return (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Taxes</p>
                              <table className="w-full text-xs">
                                <tbody>
                                  {allTaxes.map(tax => {
                                    const color = refundableCodes.has(tax.taxCode) ? 'text-green-600' : 'text-red-500'
                                    return (
                                      <tr key={tax.taxCode}>
                                        <td className={`${color} pr-4 py-0.5`}>{tax.taxCode}</td>
                                        <td className={`${color} text-right`}>
                                          {tax.amount} {tax.currencyCode}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )
                        }
                        if (ticket.isRefundable && ticket.refundTaxes && ticket.refundTaxes.length > 0) {
                          return (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Taxes</p>
                              <table className="w-full text-xs">
                                <tbody>
                                  {ticket.refundTaxes.map(tax => (
                                    <tr key={tax.taxCode}>
                                      <td className="text-green-600 pr-4 py-0.5">{tax.taxCode}</td>
                                      <td className="text-green-600 text-right">{tax.amount}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )
                        }
                        return null
                      })()}

                      {ticket.isRefundable && ticket.refundTotals && (
                        <div className="border-t border-gray-200 pt-2 space-y-1 text-sm">
                          <div className="grid grid-cols-2 gap-x-4">
                            <span className="text-gray-500">Total Refundable Base Fare</span>
                            <span className="text-gray-800 text-right">
                              {ticket.refundTotals.subtotal} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4">
                            <span className="text-gray-500">Total Refundable Taxes</span>
                            <span className="text-gray-800 text-right">
                              {ticket.refundTotals.taxes} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 font-semibold">
                            <span className="text-gray-700">Total Refundable from Airline</span>
                            <span className="text-gray-900 text-right">
                              {ticket.refundTotals.total} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                          {(() => {
                            const p = (ticket.refundPenalties ?? []).find(rp => rp.applicability === timing)
                            if (!p?.penalty) return null
                            return (
                              <div className="grid grid-cols-2 gap-x-4 text-xs text-gray-400">
                                <span>
                                  Airline Cancellation Fee ({timing === 'AFTER_DEPARTURE' ? 'after dep' : 'before dep'})
                                </span>
                                <span className="text-right">
                                  {p.penalty.amount} {p.penalty.currencyCode}
                                </span>
                              </div>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Multi-ticket aggregate */}
                {(() => {
                  const sel = (refundQuoteData.tickets ?? []).filter(
                    t => selectedTicketNumbers.has(t.number) && t.isRefundable && t.refundTotals,
                  )
                  if (sel.length < 2) return null
                  const cc = sel[0].refundTotals!.currencyCode
                  const totPenalty = Object.values(ticketPenaltyMap).reduce((s, v) => s + v, 0)
                  const totBase = sel.reduce((s, t) => s + (parseFloat(t.refundTotals!.subtotal) || 0), 0)
                  const totTaxes = sel.reduce((s, t) => s + (parseFloat(t.refundTotals!.taxes) || 0), 0)
                  const totRefund = sel.reduce((s, t) => s + (parseFloat(t.refundTotals!.total) || 0), 0)

                  return (
                    <div className="mt-2 border border-indigo-200 rounded p-3 bg-indigo-50 space-y-1 text-sm">
                      <p className="text-xs font-semibold text-indigo-700 mb-2">
                        Selected Tickets ({sel.length} of {refundQuoteData.tickets?.length ?? 0})
                      </p>
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Airline Cancellation Fees</span>
                        <span className="text-gray-800 text-right">
                          {totPenalty.toFixed(2)} {cc}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Total Base Fares</span>
                        <span className="text-gray-800 text-right">
                          {totBase.toFixed(2)} {cc}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Total Refundable Taxes</span>
                        <span className="text-gray-800 text-right">
                          {totTaxes.toFixed(2)} {cc}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 font-semibold border-t border-indigo-200 pt-1 mt-1">
                        <span className="text-indigo-700">Total Refundable from Airline</span>
                        <span className="text-indigo-900 text-right">
                          {totRefund.toFixed(2)} {cc}
                        </span>
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* GDS Fare Rules */}
            <button
              type="button"
              onClick={() => setFareRulesExpanded(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 border-t border-gray-200"
            >
              <span>GDS Fare Rules</span>
              <span className="text-gray-400">{fareRulesExpanded ? '▲' : '▼'}</span>
            </button>

            {fareRulesExpanded && (
              <div className="px-3 pb-3 space-y-3">
                {refundQuoteData.structuredFareRules && refundQuoteData.structuredFareRules.length > 0 && (
                  <div className="border border-purple-200 rounded bg-purple-50 divide-y divide-purple-100">
                    <div className="px-3 py-1.5 text-xs font-semibold text-purple-800">AI Extracted Summary</div>
                    {refundQuoteData.structuredFareRules.map((r, i) => (
                      <div
                        key={i}
                        className="px-3 py-2 text-xs text-purple-900 space-y-1"
                      >
                        <div>
                          <span className="font-medium">
                            {r.fareBasis} ({r.originDestination}, {r.airline}):
                          </span>{' '}
                          {r.refundable ? 'refundable' : 'non-refundable'}
                          {r.penaltyBasis ? `, ${r.penaltyBasis.toLowerCase().replace('_', ' ')}` : ''}
                          {r.sourceCategory ? `, from Cat ${r.sourceCategory}` : ''}
                        </div>
                        {r.cancellationPenalties.length > 0 && (
                          <div>
                            Penalties:{' '}
                            {r.cancellationPenalties
                              .map(p => `${p.amount} ${p.currency} (${p.timing.replace('_', ' ').toLowerCase()})`)
                              .join(', ')}
                          </div>
                        )}
                        {r.nonRefundableTaxCodes.length > 0 && (
                          <div>Non-refundable taxes: {r.nonRefundableTaxCodes.join(', ')}</div>
                        )}
                        {(r.conditionalNonRefundableTaxCodes?.length ?? 0) > 0 && (
                          <div>
                            Non-refundable only when:{' '}
                            {r
                              .conditionalNonRefundableTaxCodes!.map(
                                c =>
                                  `${c.code} (${c.condition.replace(/_/g, ' ').toLowerCase()}${c.hours != null ? ` ${c.hours}h` : ''})`,
                              )
                              .join(', ')}
                          </div>
                        )}
                        {r.hasUnverifiablePenaltyClause && (
                          <div className="text-amber-700 font-medium">
                            ⚠ Unverifiable clause flagged — see banner above.
                          </div>
                        )}
                        {r.notes && <div className="text-purple-700 italic">{r.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {refundQuoteData.fareRulesError && (
                  <p className="text-xs text-red-500">Failed to load fare rules from GDS.</p>
                )}
                {refundQuoteData.fareRules && refundQuoteData.fareRules.length > 0
                  ? refundQuoteData.fareRules.map((fc, fcIdx) => (
                      <div
                        key={fcIdx}
                        className="border border-gray-200 rounded bg-white"
                      >
                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                          <span className="text-xs font-semibold text-gray-700">{fc.originDestination}</span>
                          <span className="ml-2 text-xs text-gray-500">{fc.fareBasis}</span>
                          <span className="ml-2 text-xs text-gray-400">{fc.airline}</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {fc.rules.map((rule, rIdx) => (
                            <details
                              key={rIdx}
                              className="group"
                            >
                              <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50">
                                <span>
                                  {rule.title}
                                  {rule.category ? (
                                    <span className="ml-1 text-gray-400">(Cat {rule.category})</span>
                                  ) : null}
                                </span>
                                <span className="text-gray-400 group-open:rotate-90 transition-transform">▶</span>
                              </summary>
                              <pre className="px-3 pb-3 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                {rule.text}
                              </pre>
                            </details>
                          ))}
                        </div>
                      </div>
                    ))
                  : !refundQuoteData.fareRulesError && (
                      <p className="text-xs text-gray-400">No fare rules available.</p>
                    )}
              </div>
            )}

            {/* GDS Refund Totals */}
            <button
              type="button"
              onClick={() => setRefundTotalsExpanded(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 border-t border-gray-200"
            >
              <span>GDS Refund Totals</span>
              <span className="text-gray-400">{refundTotalsExpanded ? '▲' : '▼'}</span>
            </button>

            {refundTotalsExpanded &&
              (() => {
                const sel = (refundQuoteData.tickets ?? []).filter(t => selectedTicketNumbers.has(t.number))
                const totPenalty = Object.values(ticketPenaltyMap).reduce((s, v) => s + v, 0)
                const totBase = sel.reduce((s, t) => s + (parseFloat(t.refundTotals?.subtotal ?? '0') || 0), 0)
                const totTaxes = sel.reduce((s, t) => s + (parseFloat(t.refundTotals?.taxes ?? '0') || 0), 0)
                const totRF = sel.reduce((s, t) => s + (parseFloat(t.refundTotals?.total ?? '0') || 0), 0)
                const refundTotalsCc =
                  sel.find(t => t.refundTotals?.currencyCode)?.refundTotals?.currencyCode ?? currency

                return (
                  <div className="px-3 py-3 space-y-1 text-sm">
                    <div className="grid grid-cols-2 gap-x-4">
                      <span className="text-gray-500">Ticket Base Fare</span>
                      <span className="text-gray-800 text-right">
                        {refundTotalsCc} {totBase.toFixed(2)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                      <span className="text-gray-500">Airline Cancellation Fees</span>
                      <span className="text-gray-800 text-right">
                        {currency} {totPenalty.toFixed(2)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                      <span className="text-gray-500">Total Refundable Taxes</span>
                      <span className="text-gray-800 text-right">
                        {refundTotalsCc} {totTaxes.toFixed(2)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 font-semibold border-t border-gray-200 pt-1 mt-1">
                      <span className="text-green-700">Total Refundable from Airline</span>
                      <span className="text-green-900 text-right">
                        {refundTotalsCc} {totRF.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )
              })()}
          </div>
        )}

        {/* Financial Summary */}
        {/* A restricted/secured booking has no readable GDS data — the access banner above stays
            visible as a standing warning, but the operator can still enter a manual quote once
            access is confirmed with the account manager, same as partialData/refundabilityUnavailable. */}
        {showQuoteUi && bookingDetails && (!isAlreadyProcessed || forceWorkflowOverride) && awaitingFareRules && (
          <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded-md">
            <p className="text-sm text-blue-800 animate-pulse">Checking the airline's fare rules…</p>
            <p className="text-xs text-blue-700 mt-1">
              The refund figures and confidence appear once they're in, because the filed rule can change the penalty.
            </p>
          </div>
        )}
        {showQuoteUi && bookingDetails && (!isAlreadyProcessed || forceWorkflowOverride) && !awaitingFareRules && (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">Financial Summary</span>
              {isAllSelectedNonRefundable && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300">
                  Non-refundable
                </span>
              )}
              {isTaxesOnlyRefund && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                  Non-refundable - partial taxes only
                </span>
              )}
            </div>
            <div className="px-3 py-3 space-y-1 text-sm">
              {/* Blockers and caveats stay above the fold — never hidden behind the breakdown toggle. */}
              {refundQuoteData?.fareRulesError && (
                <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800 font-medium">
                  Fare rules unavailable: the airline penalty wasn't checked against the filed rule. Confirm it before
                  processing.
                </div>
              )}
              {requiresManualDeductions && (
                <div className="px-2 py-1.5 bg-red-50 border border-red-300 rounded text-xs text-red-800 font-medium">
                  Airline penalty unknown — no usable source (Sabre quote, ticket penalties, fare rules). Enter the
                  confirmed Airline Cancellation Fee or the Airline Refund to PLT in the overrides below before
                  processing.
                </div>
              )}
              {quote && quote.warnings.length > 0 && (
                <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800 space-y-0.5">
                  {quote.warnings.map((w, i) => (
                    <p key={i}>{w}</p>
                  ))}
                </div>
              )}
              {netFareTtvEstimate && (
                <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                  Net Fare estimated from TTV (PipeDrive net fare cost unavailable) — verify and correct if needed.
                </div>
              )}
              {hasNonAutomatedRefundableTickets &&
                !hasForeignPenaltyCurrencyMismatch &&
                !hasForeignCurrencyAutoConverted && (
                  <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                    Carrier does not participate in automated refunds — Supplier Cash Refund is pre-filled as an
                    estimate (net fare − penalty + taxes). Verify the exact amount with consolidator before processing,
                    as their commission/fee may differ slightly.
                  </div>
                )}
              {hasForeignCurrencyAutoConverted && (
                <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                  Fare priced in a foreign currency — amounts auto-converted to {currency} using the fare construction
                  rate ({refundQuoteData?.fareToBookingCurrencyRate?.toFixed(4)}). Verify with consolidator before
                  processing.
                </div>
              )}
              {hasForeignPenaltyCurrencyMismatch && (
                <div className="px-2 py-1.5 bg-red-50 border border-red-300 rounded text-xs text-red-800">
                  Penalty returned in a foreign currency (not {currency}) and no conversion rate could be derived. Enter
                  the correct {currency} amounts manually using the overrides below.
                </div>
              )}
              {isAllSelectedNonRefundable && (
                <div className="px-2 py-1.5 bg-blue-50 border border-blue-300 rounded text-xs text-blue-800">
                  Tickets confirmed non-refundable by GDS. No airline refund expected. Customer may still be entitled to
                  a refund under Australian Consumer Law — verify with consolidator if applicable.
                </div>
              )}
              {isAtcoPenaltyFallback && (
                <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                  Penalty from ATPCO fare rules — automated refunds check failed for this booking (e.g. exchanged
                  ticket). Verify with consolidator before processing.
                </div>
              )}
              {hasPaxCountMismatch && (
                <div className="px-2 py-1.5 bg-red-50 border border-red-300 rounded text-xs text-red-800">
                  Pipedrive records {bookingDetails?.travellersTotal} traveller
                  {bookingDetails?.travellersTotal === 1 ? '' : 's'}, but GDS ticket data shows {gdsTotalPax} — verify
                  no passengers were dropped before processing.
                </div>
              )}
              {hasSupplierCashRefundMismatch && (
                <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                  Pipedrive recorded a supplier refund of {pdSupplierCashRefund?.toFixed(2)} {currency}, but our
                  calculation estimates {computedSupplierCashRefund?.toFixed(2)} {currency} — verify with consolidator
                  before finalizing.
                </div>
              )}

              {/* The headline numbers an agent reads out on a call. Everything else is workings. */}
              <div className="grid grid-cols-2 gap-x-4">
                <span className="text-gray-500">Customer Paid</span>
                <span className="text-gray-800 text-right tabular-nums">
                  {(statement?.paxLedger.totalPaid ?? 0).toFixed(2)} {currency}
                </span>
              </div>

              {bookingDetails.payment?.pastDue != null && bookingDetails.payment.pastDue > 0 && (
                <div className="grid grid-cols-2 gap-x-4">
                  <span className="text-orange-600">Past Due</span>
                  <span className="text-orange-700 text-right tabular-nums">
                    {bookingDetails.payment.pastDue.toFixed(2)} {currency}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-4">
                <span className="text-gray-500">
                  Airline Penalties
                  {deductions && (
                    <span
                      className={`ml-1 text-xs font-normal ${isAtcoPenaltyFallback ? 'text-amber-600' : 'text-gray-400'}`}
                    >
                      ({deductions.source.toLowerCase().replace(/_/g, ' ')})
                    </span>
                  )}
                </span>
                <span
                  className={`text-right tabular-nums ${airlinePenaltyAmount > 0 ? 'text-red-600' : 'text-gray-800'}`}
                >
                  {airlinePenaltyAmount > 0 ? '-' : ''}
                  {airlinePenaltyAmount.toFixed(2)} {currency}
                </span>
              </div>

              {/* Taxes the airline simply doesn't return — not a fee it charges, so it's broken
                  out from Airline Penalties above rather than folded into it. */}
              {(deductions?.nonRefundableTaxes ?? 0) > 0 && (
                <div className="grid grid-cols-2 gap-x-4">
                  <span className="text-gray-500">Non-Refundable Amount</span>
                  <span className="text-red-600 text-right tabular-nums">
                    -{(deductions?.nonRefundableTaxes ?? 0).toFixed(2)} {currency}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-4">
                <span className="text-gray-500">
                  Our Fees <span className="text-xs text-gray-400">(PLT termination fee, to be collected)</span>
                </span>
                <span className="text-red-600 text-right tabular-nums">
                  -{(quote?.pltFee ?? 0).toFixed(2)} {currency}
                </span>
              </div>

              <div
                className={`grid grid-cols-2 gap-x-4 font-semibold border-t border-gray-200 pt-1 mt-1 ${statement?.isShortfall ? 'text-red-700' : 'text-green-700'}`}
              >
                <span>{statement?.isShortfall ? 'Customer Owes (shortfall)' : 'Money Back to Customer'}</span>
                <span className="text-right tabular-nums">
                  {(statement?.isShortfall ? Math.abs(statement.paxLedger.raw) : statement?.refundToPax ?? 0).toFixed(
                    2,
                  )}{' '}
                  {currency}
                </span>
              </div>

              {/* PLT-side, not customer-facing — but it's the figure ops reconciles against the
                  consolidator, so it belongs above the fold rather than inside the breakdown. */}
              <div className="grid grid-cols-2 gap-x-4 font-medium border-t border-dashed border-gray-200 pt-1 mt-1">
                <span className="text-gray-700">
                  Expected Supplier Refund <span className="text-xs font-normal text-gray-400">(net cash in)</span>
                </span>
                <span className="text-gray-800 text-right tabular-nums">
                  {(statement?.expectedSupplierRefund ?? 0).toFixed(2)} {currency}
                </span>
              </div>

              {/* Diagnostic: what we'd expect if the carrier refunds its YQ/YR surcharges (which our
                  estimate withholds). Shown only when that assumption is actually in play — see the
                  DAALOR/NHZJXK Qantas cases where the airline refunded the YR. Display only. */}
              {quote?.surchargePolicy && (
                <p className="text-xs text-gray-400 -mt-0.5 pl-3">
                  YQ/YR {quote.surchargePolicy === 'refunds' ? 'refunded' : 'kept'} by{' '}
                  {refundQuoteData?.carrier ?? 'this carrier'} on refundable fares (YQ/YR table)
                </p>
              )}
              {statement && deductions && deductions.floorTaxesWithheld > 0 && (
                <div className="grid grid-cols-2 gap-x-4 text-xs text-gray-400 -mt-0.5">
                  <span className="pl-3">
                    * w/out YR/YQ <span className="text-gray-300">(if carrier refunds surcharges)</span>
                  </span>
                  <span className="text-right tabular-nums">
                    {Math.max(0, statement.supplierLedger.raw + deductions.floorTaxesWithheld).toFixed(2)} {currency}
                  </span>
                </div>
              )}

              {confidence && (
                <div
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${confidenceStyle[confidence.level].badge}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${confidenceStyle[confidence.level].dot}`} />
                  <span className="font-medium capitalize">{confidence.level} confidence</span>
                  <span className="text-gray-400">({confidence.score}/100)</span>
                </div>
              )}

              {/* Doubts with a known dollar effect (refund-quote SPEC.md §10.8): the range they span and
                  their cause, and whether they change what happens to the case. */}
              {quote && quote.uncertainty.issues.length > 0 && (
                <div
                  className={`text-xs rounded border px-2 py-1.5 space-y-1 ${quote.uncertainty.settled ? 'border-gray-200 bg-gray-50 text-gray-600' : 'border-amber-300 bg-amber-50 text-amber-800'}`}
                >
                  <p className="font-medium">
                    {quote.uncertainty.settled
                      ? quote.uncertainty.withinTolerance
                        ? 'Uncertain, but within tolerance'
                        : "Uncertain, but it doesn't change the case"
                      : 'Fact-check before processing'}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 tabular-nums">
                    <span>Supplier refund</span>
                    <span className="text-right">
                      {quote.uncertainty.supplierRefund.low.toFixed(2)}–
                      {quote.uncertainty.supplierRefund.high.toFixed(2)} {currency}
                    </span>
                    <span>Refund to customer</span>
                    <span className="text-right">
                      {quote.uncertainty.refundToPax.low.toFixed(2)}–{quote.uncertainty.refundToPax.high.toFixed(2)}{' '}
                      {currency}
                    </span>
                  </div>
                  <ul className="list-disc pl-4">
                    {quote.uncertainty.issues.map(issue => (
                      <li key={issue.code}>{issue.label}</li>
                    ))}
                  </ul>
                  {!quote.uncertainty.settled && (
                    <p>
                      Within this range{' '}
                      {quote.uncertainty.outcomeChanges
                        .map(
                          outcome =>
                            ({
                              'customer-owes': 'the customer could owe money instead of getting a refund',
                              'nothing-from-supplier': 'the supplier could send nothing back',
                              'travel-credit': 'it could come back as a travel credit, not cash',
                            })[outcome],
                        )
                        .concat(
                          quote.uncertainty.spread > quote.uncertainty.spreadCap
                            ? [
                                `the range spans ${quote.uncertainty.spread.toFixed(2)} ${currency}, more than the ${quote.uncertainty.spreadCap.toFixed(2)} a high quote may carry`,
                              ]
                            : [],
                        )
                        .join(', and ')}
                      .
                    </p>
                  )}
                </div>
              )}

              {confidence && (
                <details className="text-xs text-gray-600">
                  <summary className="cursor-pointer select-none">How was this scored?</summary>
                  <ul className="mt-1 space-y-1 border border-gray-200 rounded-md p-2 bg-gray-50">
                    {confidence.breakdown.map(item => (
                      <li
                        key={item.label}
                        className="flex justify-between gap-3"
                      >
                        <span>{item.label}</span>
                        <span className="font-mono tabular-nums shrink-0">
                          {item.points > 0 ? `+${item.points}` : item.points < 0 ? `−${-item.points}` : '0'}
                        </span>
                      </li>
                    ))}
                    <li className="flex justify-between gap-3 border-t border-gray-200 pt-1 font-medium">
                      <span>Score (limited to 0–100)</span>
                      <span className="font-mono tabular-nums shrink-0">{confidence.score}</span>
                    </li>
                  </ul>
                </details>
              )}

              {confidence && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setConfidenceOverrideOpen(v => !v)
                      setConfidenceOverrideSuccess(false)
                      setConfidenceOverrideError('')
                    }}
                    className="text-xs px-2 py-0.5 rounded border bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200"
                  >
                    {confidenceOverrideOpen ? 'Cancel override' : 'Override confidence'}
                  </button>
                  {confidenceOverrideSuccess && !confidenceOverrideOpen && (
                    <span className="text-xs text-green-700">Saved as {confidenceOverrideLevel}.</span>
                  )}
                </div>
              )}

              {confidence && confidenceOverrideOpen && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md flex-wrap">
                  <select
                    value={confidenceOverrideLevel}
                    onChange={e =>
                      setConfidenceOverrideLevel(
                        (e.target as HTMLSelectElement).value as typeof confidenceOverrideLevel,
                      )
                    }
                    className="text-xs border-gray-300 rounded"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                    <option value="none">None</option>
                  </select>
                  <button
                    type="button"
                    disabled={confidenceOverrideLoading || !refundQuoteData?.cancellationRequestId}
                    onClick={async () => {
                      const cancellationRequestId = refundQuoteData?.cancellationRequestId
                      if (!cancellationRequestId) return
                      setConfidenceOverrideLoading(true)
                      setConfidenceOverrideError('')
                      try {
                        const resp = await fetch(`${config.apiBaseUrl}/bookings/${bookingId}/refund-quote/confidence`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: { 'content-type': 'application/json' },
                          body: JSON.stringify({
                            cancellationRequestId,
                            confidenceLevel: confidenceOverrideLevel,
                            confidenceReportKind: 'override',
                          }),
                        })
                        if (!resp.ok) {
                          const d = await resp.json().catch(() => ({}))
                          throw new Error(d.error ?? `Request failed (${resp.status})`)
                        }
                        // Stops every automatic send for this quote, so a later one can't replace the override.
                        confidenceLog.current = lockConfidenceReports(confidenceLog.current, cancellationRequestId)
                        setConfidenceOverrideSuccess(true)
                        setConfidenceOverrideOpen(false)
                      } catch (err) {
                        setConfidenceOverrideError(err instanceof Error ? err.message : 'Unknown error')
                      } finally {
                        setConfidenceOverrideLoading(false)
                      }
                    }}
                    className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {confidenceOverrideLoading ? 'Saving…' : 'Save override'}
                  </button>
                  {!refundQuoteData?.cancellationRequestId && (
                    <span className="text-xs text-gray-400">No recorded quote to attach this to yet.</span>
                  )}
                  {confidenceOverrideError && <span className="text-xs text-red-600">{confidenceOverrideError}</span>}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1.5">
                <input
                  type="checkbox"
                  id="financial-breakdown"
                  checked={financialBreakdownExpanded}
                  onChange={e => setFinancialBreakdownExpanded((e.target as HTMLInputElement).checked)}
                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-teal-600"
                />
                <label
                  htmlFor="financial-breakdown"
                  className="text-xs text-gray-500 cursor-pointer select-none"
                >
                  Show breakdown
                </label>
              </div>

              {financialBreakdownExpanded && (
                <div className="space-y-1 border-t border-dashed border-gray-200 pt-2 mt-1">
                  <div className="grid grid-cols-2 gap-x-4">
                    <span className="text-gray-500">Plan Total / TTV (PipeDrive)</span>
                    <span className="text-gray-800 text-right tabular-nums">
                      {parseFloat(salesPrice || '0').toFixed(2)} {currency}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4">
                    <span className="text-gray-500">Amount Paid (incl. deposit)</span>
                    <span className="text-gray-800 text-right tabular-nums">
                      {parseFloat(totalPaidAmount || '0').toFixed(2)} {currency}
                    </span>
                  </div>

                  {bookingDetails.payment?.discountAmount != null && bookingDetails.payment.discountAmount > 0 && (
                    <div className="grid grid-cols-2 gap-x-4">
                      <span className="text-gray-500">Discount</span>
                      <span className="text-gray-800 text-right tabular-nums">
                        - {bookingDetails.payment.discountAmount.toFixed(2)} {currency}
                      </span>
                    </div>
                  )}

                  {/* What the airline keeps, itemised. */}
                  {deductions && deductions.total > 0 && (
                    <div className="pl-4 space-y-0.5 text-xs text-gray-400">
                      {/* Source 5 doesn't produce a penalty — it forfeits the whole base fare.
                          Calling that a "cancellation penalty" reads as a fee the airline charged. */}
                      <div className="grid grid-cols-2 gap-x-4">
                        <span>
                          {deductions.source === 'NON_REFUNDABLE_FARE' || deductions.source === 'AIRLINE_POLICY'
                            ? 'Base fare forfeited (airline keeps it all)'
                            : 'Cancellation penalty'}
                        </span>
                        <span className="text-right tabular-nums">
                          {deductions.penalty.toFixed(2)} {currency}
                        </span>
                      </div>
                      {deductions.nonRefundableTaxes > 0 && (
                        <div className="grid grid-cols-2 gap-x-4">
                          <span>Non-refundable taxes</span>
                          <span className="text-right tabular-nums">
                            {deductions.nonRefundableTaxes.toFixed(2)} {currency}
                          </span>
                        </div>
                      )}
                      {deductions.adminFee > 0 && (
                        <div className="grid grid-cols-2 gap-x-4">
                          <span>
                            Airline admin fee
                            {refundQuoteData?.refundAdminFeeSource?.startsWith('text-parser') && (
                              <span className="ml-1 text-amber-600">(parsed — verify)</span>
                            )}
                          </span>
                          <span className="text-right tabular-nums">
                            {deductions.adminFee.toFixed(2)} {currency}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Why the badge above says what it says — plain-English, safe to read out to ops
                      without any GDS background. See confidence-score.ts for the underlying rules. */}
                  {confidence && confidence.factors.length > 0 && (
                    <div className="pl-4 space-y-0.5 text-xs text-gray-400 border-t border-dashed border-gray-200 pt-1 mt-1">
                      <span className="text-gray-500">Why this confidence level:</span>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {confidence.factors.map((factor, i) => (
                          <li key={i}>{factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Supplier side — cash PLT gets back. Never touches the customer figure above. */}
                  <div className="grid grid-cols-2 gap-x-4 border-t border-dashed border-gray-200 pt-1 mt-1">
                    <span className="text-gray-500">
                      Nett Paid to Supplier
                      {quote && (
                        <span className="ml-1 text-xs font-normal text-gray-400">
                          ({quote.nettPaid.basis.toLowerCase().replace(/_/g, ' ')})
                        </span>
                      )}
                    </span>
                    <span className="text-gray-800 text-right tabular-nums">
                      {(quote?.nettPaid.total ?? 0).toFixed(2)} {currency}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4">
                    <span className="text-gray-500">
                      Gross Airline Refund
                      {isTaxesOnlyRefund && (
                        <span className="ml-1 text-xs font-normal text-gray-400">(Taxes only)</span>
                      )}
                    </span>
                    <span className="text-green-700 text-right tabular-nums">
                      +{(quote?.grossAirlineRefund ?? 0).toFixed(2)} {currency}
                    </span>
                  </div>
                  {(quote?.supplierFee ?? 0) > 0 && (
                    <>
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">
                          Supplier Fee <span className="text-xs text-gray-400">(consolidator)</span>
                        </span>
                        <span className="text-red-600 text-right tabular-nums">
                          -{quote!.supplierFee.toFixed(2)} {currency}
                        </span>
                      </div>
                      {(quote!.supplierFeeBreakdown.commissionClawback > 0 ||
                        quote!.supplierFeeBreakdown.gst > 0 ||
                        quote!.supplierFeeBreakdown.processingFees > 0) && (
                        <div className="pl-4 space-y-0.5 text-xs text-gray-400">
                          <div className="grid grid-cols-2 gap-x-4">
                            <span>Flat fee</span>
                            <span className="text-right tabular-nums">
                              {quote!.supplierFeeBreakdown.flatFee.toFixed(2)} {currency}
                            </span>
                          </div>
                          {quote!.supplierFeeBreakdown.commissionClawback > 0 && (
                            <div className="grid grid-cols-2 gap-x-4">
                              <span>Commission claw-back</span>
                              <span className="text-right tabular-nums">
                                {quote!.supplierFeeBreakdown.commissionClawback.toFixed(2)} {currency}
                              </span>
                            </div>
                          )}
                          {quote!.supplierFeeBreakdown.gst > 0 && (
                            <div className="grid grid-cols-2 gap-x-4">
                              <span>GST</span>
                              <span className="text-right tabular-nums">
                                {quote!.supplierFeeBreakdown.gst.toFixed(2)} {currency}
                              </span>
                            </div>
                          )}
                          {quote!.supplierFeeBreakdown.processingFees > 0 && (
                            <div className="grid grid-cols-2 gap-x-4">
                              <span>Processing fee (not refunded)</span>
                              <span className="text-right tabular-nums">
                                {quote!.supplierFeeBreakdown.processingFees.toFixed(2)} {currency}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {statement && quote && (
                    <>
                      <div className="grid grid-cols-2 gap-x-4 border-t border-dashed border-gray-200 pt-1 mt-1">
                        <span className="text-gray-500">
                          PLT keeps <span className="text-xs text-gray-400">(our fee − supplier fee)</span>
                        </span>
                        <span className="text-gray-800 text-right tabular-nums">
                          {roundCurrency(quote.pltFee - quote.supplierFee).toFixed(2)} {currency}
                        </span>
                      </div>
                      {/* The two ledgers must differ by exactly (paid − cost) − our fee + supplier fee.
                          Agents don't need the figure, only that it reconciles — so show a verdict. */}
                      {(() => {
                        const expected = roundCurrency(
                          statement.paxLedger.totalPaid -
                            statement.supplierLedger.nettPaid -
                            quote.pltFee +
                            quote.supplierFee,
                        )
                        const balances = Math.abs(statement.margin - expected) <= 0.02
                        return (
                          <div className="grid grid-cols-2 gap-x-4 text-xs">
                            <span className="text-gray-400">Both sides reconcile</span>
                            <span
                              className={`text-right tabular-nums ${balances ? 'text-green-600' : 'text-red-600 font-semibold'}`}
                            >
                              {balances
                                ? '✓ balanced'
                                : `✗ out by ${Math.abs(statement.margin - expected).toFixed(2)} — do not process`}
                            </span>
                          </div>
                        )
                      })()}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calculation Form */}
        {showQuoteUi && bookingDetails && (
          <fieldset
            disabled={isAlreadyProcessed && !forceWorkflowOverride}
            className={`space-y-4 border-none p-0 m-0 min-w-0${isAlreadyProcessed && !forceWorkflowOverride ? ' opacity-60' : ''}`}
          >
            {noRefundDataWarning && (
              <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
                <p className="text-sm font-semibold text-amber-800">No airline refund data loaded yet</p>
                <p className="text-sm text-amber-700 mt-1">
                  Retrieve the refund quote from GDS above, or enter net fare and supplier cash refund manually below.
                  <strong>Net Fare must always equal what PLT paid for the ticket</strong> (from PipeDrive net fare
                  cost), not the refund amount. For taxes-only refunds the GDS populates Supplier Cash Refund
                  automatically — do not set Net Fare to 0.
                </p>
              </div>
            )}

            {/* Sales Price */}
            <div>
              <label
                htmlFor="salesPrice"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Sales Price / TTV ({currency})
              </label>
              <input
                id="salesPrice"
                type="number"
                step="0.01"
                value={salesPrice}
                onChange={e => setSalesPrice((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
              />
            </div>

            {/* Pax breakdown */}
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="adultsCount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Adults cancelling
                </label>
                <input
                  id="adultsCount"
                  type="number"
                  min="0"
                  value={adultsCount}
                  onChange={e => setAdultsCount((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label
                  htmlFor="childrenCount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Children cancelling
                </label>
                <input
                  id="childrenCount"
                  type="number"
                  min="0"
                  value={childrenCount}
                  onChange={e => setChildrenCount((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Net Fare per pax */}
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="netFareAdult"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Net Fare Adult per pax ({currency})
                  {hasGdsData && !netFareTtvEstimate && (
                    <span className="ml-1 text-xs font-normal text-gray-400">(avg from GDS)</span>
                  )}
                  {netFareTtvEstimate && (
                    <span className="ml-1 text-xs font-normal text-amber-500">(est. from TTV)</span>
                  )}
                </label>
                <input
                  id="netFareAdult"
                  type="number"
                  step="0.01"
                  value={netFareAdult}
                  onChange={e => {
                    setNetFareAdult((e.target as HTMLInputElement).value)
                    setIsNetFareManuallyEdited(true)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label
                  htmlFor="netFareChild"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Net Fare Child per pax ({currency})
                </label>
                <input
                  id="netFareChild"
                  type="number"
                  step="0.01"
                  value={netFareChild}
                  onChange={e => {
                    setNetFareChild((e.target as HTMLInputElement).value)
                    setIsNetFareManuallyEdited(true)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Total Net Fare (derived) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Net Fare ({currency}){' '}
                <span className="text-xs font-normal text-gray-500">(adults × ADT fare + children × CHD fare)</span>
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800">
                {currency} {(quote?.nettPaid.total ?? 0).toFixed(2)}
              </div>
            </div>

            {/* Total Paid */}
            <div>
              <label
                htmlFor="totalPaidAmount"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Amount Paid by Customer ({currency}){' '}
                <span className="text-sm font-normal text-gray-500">
                  Includes deposit. Manual invoices not included.
                </span>
              </label>
              <input
                id="totalPaidAmount"
                type="number"
                step="0.01"
                value={totalPaidAmount}
                onChange={e => setTotalPaidAmount((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
              />
            </div>

            {/* Airline Cancellation Fee override — always shown to allow operators to adjust carrier-specific penalties */}
            {true && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label
                    htmlFor="airlineCancellationFeeOverride"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Airline Cancellation Fee ({currency}){' '}
                    {hasForeignPenaltyCurrencyMismatch ? (
                      <span className="text-xs font-normal text-red-600">
                        (foreign currency — enter {currency} amount)
                      </span>
                    ) : hasForeignCurrencyAutoConverted ? (
                      <span className="text-xs font-normal text-amber-600">
                        (auto-converted from foreign currency — verify and enter correct {currency} amount)
                      </span>
                    ) : isAtcoPenaltyFallback ? (
                      <span className="text-xs font-normal text-amber-600">
                        (ATPCO estimate — override if confirmed)
                      </span>
                    ) : (
                      <span className="text-xs font-normal text-gray-500">(override if confirmed)</span>
                    )}
                  </label>
                  {airlineCancellationFeeOverride !== '' && (
                    <button
                      type="button"
                      onClick={() => setAirlineCancellationFeeOverride('')}
                      className="text-xs px-2 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300"
                    >
                      Override active — click to revert
                    </button>
                  )}
                </div>
                <input
                  id="airlineCancellationFeeOverride"
                  type="number"
                  step="0.01"
                  value={
                    airlineCancellationFeeOverride !== ''
                      ? airlineCancellationFeeOverride
                      : (deductions ? deductions.penalty + deductions.nonRefundableTaxes : 0).toFixed(2)
                  }
                  onChange={e => setAirlineCancellationFeeOverride((e.target as HTMLInputElement).value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none ${
                    airlineCancellationFeeOverride !== ''
                      ? 'border-amber-300 focus:ring-amber-500 focus:border-amber-500 bg-amber-50'
                      : hasForeignPenaltyCurrencyMismatch
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  placeholder="0.00"
                />
                <p className="mt-0.5 text-xs text-gray-400">
                  {hasForeignCurrencyAutoConverted
                    ? "Penalty auto-converted using fare construction rate — enter the correct amount in the consolidator's confirmed currency."
                    : 'Estimated from ATPCO fare rules or ticket penalty data. Enter the confirmed penalty from consolidator if different.'}
                </p>
              </div>
            )}

            {/* Supplier Cash Refund (Airline Refund to PLT) with override */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor="supplierCashRefund"
                  className="block text-sm font-medium text-gray-700"
                >
                  Airline Refund to PLT ({currency}){' '}
                  <span className="text-xs font-normal text-gray-500">
                    {hasGdsData ? '(from GDS "Total Refundable from Airline")' : '(from supplier portal — ETG/CTG)'}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    // Start the override from our own figure. Pipedrive's refund field is the NET amount a
                    // previous run wrote back, and this field is the GROSS airline refund.
                    if (!isSupplierCashRefundOverridden) setSupplierCashRefund(displayGrossRefund)
                    setIsSupplierCashRefundOverridden(v => !v)
                  }}
                  className={`text-xs px-2 py-0.5 rounded border ${isSupplierCashRefundOverridden ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                >
                  {isSupplierCashRefundOverridden ? 'Override active — click to revert' : 'Override'}
                </button>
              </div>
              <input
                id="supplierCashRefund"
                type="number"
                step="0.01"
                value={displayGrossRefund}
                onChange={e => {
                  setIsSupplierCashRefundOverridden(true)
                  setSupplierCashRefund((e.target as HTMLInputElement).value)
                }}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none ${isSupplierCashRefundOverridden ? 'border-amber-300 focus:ring-amber-500 focus:border-amber-500 bg-amber-50' : requiresManualDeductions ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                placeholder="0.00"
              />
              <p className="mt-0.5 text-xs text-gray-400">
                GROSS refund from the airline, before the consolidator fee. Taxes-only tickets: the refundable taxes.
                Entering a figure implies the airline deductions (NettPaid − this amount).
              </p>
            </div>

            {/* Airline Admin Fee — shown whenever the backend resolved a fee or the carrier is known */}
            {(adminFee > 0 || refundQuoteData?.refundAdminFee !== undefined) &&
              !isVoidWindow &&
              !refundQuoteData?.noTicketsIssued && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label
                      htmlFor="adminFee"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Airline Admin Fee ({currency}){' '}
                      <span className="text-xs font-normal text-gray-500">(out-of-GDS — BSP debit memo)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (isAdminFeeOverridden) {
                          setAdminFee(refundQuoteData?.refundAdminFee ?? 0)
                        }
                        setIsAdminFeeOverridden(v => !v)
                      }}
                      className={`text-xs px-2 py-0.5 rounded border ${isAdminFeeOverridden ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                    >
                      {isAdminFeeOverridden ? 'Override active — click to revert' : 'Override'}
                    </button>
                  </div>
                  <input
                    id="adminFee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={adminFee}
                    onChange={e => {
                      setIsAdminFeeOverridden(true)
                      setAdminFee(parseFloat((e.target as HTMLInputElement).value) || 0)
                    }}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none ${isAdminFeeOverridden ? 'border-amber-300 focus:ring-amber-500 focus:border-amber-500 bg-amber-50' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                    placeholder="0.00"
                  />
                  {refundQuoteData?.refundAdminFeeSource?.startsWith('text-parser') && (
                    <p className="mt-0.5 text-xs text-amber-600">
                      Fee detected from unstructured fare rule text — verify amount with consolidator before processing.
                    </p>
                  )}
                </div>
              )}

            {/* PLT Termination Fee with override */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor="pltFee"
                  className="block text-sm font-medium text-gray-700"
                >
                  PLT Termination Fee ({currency}){' '}
                  {!isPltFeeOverridden && (
                    <span className="text-xs font-normal text-gray-500">(min($150 × pax, 10% × TTV))</span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (!isPltFeeOverridden) setPltFeeOverride((quote?.pltFee ?? 0).toFixed(2))
                    setIsPltFeeOverridden(v => !v)
                  }}
                  className={`text-xs px-2 py-0.5 rounded border ${isPltFeeOverridden ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                >
                  {isPltFeeOverridden ? 'Override active — click to revert' : 'Override'}
                </button>
              </div>
              {isPltFeeOverridden ? (
                <input
                  id="pltFee"
                  type="number"
                  step="0.01"
                  value={pltFeeOverride}
                  onChange={e => setPltFeeOverride((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 bg-amber-50"
                  placeholder="0.00"
                />
              ) : (
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800">
                  {currency} {(quote?.pltFee ?? 0).toFixed(2)}
                </div>
              )}
            </div>

            {/* Net Refund result */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {statement?.isShortfall ? 'Shortfall — Customer Owes PLT' : 'Refund to Customer'}{' '}
                <span className="text-xs font-normal text-gray-500">(paid − airline deductions − PLT fee)</span>
              </label>
              <div
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-lg font-semibold ${statement?.isShortfall ? 'bg-red-50 text-red-800 border-red-200' : 'bg-green-50 text-green-800 border-green-200'}`}
              >
                {currency}{' '}
                {(statement?.isShortfall ? Math.abs(statement.paxLedger.raw) : statement?.refundToPax ?? 0).toFixed(2)}
              </div>
            </div>

            {/* Cancellation Reason */}
            <div>
              <label
                htmlFor="cancellationReason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason for Cancellation
              </label>
              <select
                id="cancellationReason"
                value={cancellationReason}
                onChange={e => setCancellationReason((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a reason</option>
                {CANCELLATION_REASONS.map(r => (
                  <option
                    key={r}
                    value={r}
                  >
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/* Supplier Notification for Cancellation Reason — Voluntary/Involuntary, added to supplier email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Notification for Cancellation Reason <span className="text-red-500">*</span>{' '}
                <span className="text-xs font-normal text-gray-500">(added to supplier refund email)</span>
              </label>
              <div className="flex items-center gap-6">
                {(['voluntary', 'involuntary'] as const).map(value => (
                  <label
                    key={value}
                    className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="supplierNotificationReason"
                      value={value}
                      checked={supplierNotificationReason === value}
                      onChange={() => setSupplierNotificationReason(value)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 cursor-pointer"
                    />
                    <span className="capitalize">{value}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Process Cancellation */}
            {((hasGdsData && refundQuoteData.tickets?.length) ||
              refundQuoteData?.noTicketsIssued ||
              refundQuoteData?.lccBooking) &&
            (!isAlreadyProcessed || forceWorkflowOverride) ? (
              <div className="pt-2 border-t border-gray-200">
                {processCancellationSuccess ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                    {refundQuoteData?.lccBooking
                      ? 'Cancellation workflow submitted — trying the cancel in Sabre. Check the workflow status above: if Sabre cannot cancel it, nothing else changes and a warning shows there.'
                      : submittedCancellationType === 'void'
                        ? 'Cancellation workflow submitted — tickets will be voided in Sabre and booking marked as Lost in Pipedrive.'
                        : refundQuoteData?.noTicketsIssued
                          ? 'Cancellation workflow submitted — the booking will be cancelled in Sabre and the Pipedrive fields updated. The deal stays open in Pipedrive, not marked as Lost.'
                          : submittedCancellationType === 'partial'
                            ? `Cancellation workflow submitted — the selected tickets will be cancelled in Sabre, Pipedrive fields updated, ${submittedNoRefundExpected ? 'with no supplier refund request or refund monitoring' : 'and refund monitoring started'}. The deal stays in its stage in Pipedrive: move it by hand.`
                            : `Cancellation workflow submitted — segments will be cancelled in Sabre, Pipedrive will be updated, ${submittedNoRefundExpected ? 'with no supplier refund request or refund monitoring' : 'and refund monitoring started'}.`}
                  </div>
                ) : processCancellationConfirm ? (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-md space-y-3">
                    <p className="text-sm font-semibold text-amber-800">Confirm automated cancellation</p>
                    <p className="text-sm text-amber-700">
                      {refundQuoteData?.lccBooking
                        ? "This will try to cancel the airline's segments in Sabre and check the PNR afterwards. If Sabre can't cancel them, the workflow stops before Pipedrive, the payment plan or any email, and you'll need to cancel in the airline portal (Agent Hub for Jetstar) or hand over to CCM."
                        : refundQuoteData?.noTicketsIssued
                          ? automatedCancellationType === 'void'
                            ? 'No tickets were issued for this booking — the airline was never charged. This will mark the booking as Lost in Pipedrive and process a full refund.'
                            : 'No tickets were issued for this booking — the airline was never charged. This will cancel the booking in Sabre, update the Pipedrive fields and process a full refund. The deal stays open in Pipedrive, not marked as Lost.'
                          : isVoidWindow
                            ? 'This will void every ticket on the booking in Sabre and mark the booking as Lost in Pipedrive. No RF supplier fields will be set.'
                            : automatedCancellationType === 'partial'
                              ? `This will cancel the selected tickets in Sabre, ${noRefundExpected ? 'skip the supplier refund request' : 'refund them'}, and update Pipedrive fields automatically. The deal stays in its stage in Pipedrive, so move it by hand afterwards.`
                              : `This will cancel the segments in Sabre, ${noRefundExpected ? 'skip the supplier refund request' : 'refund the tickets'}, and update Pipedrive fields automatically.`}
                    </p>
                    {!ticketless && (
                      <p className="text-sm text-amber-700">
                        Tickets:{' '}
                        {(refundQuoteData.tickets ?? [])
                          .filter(t => selectedTicketNumbers.has(t.number))
                          .map(t => t.number)
                          .join(', ') || 'none selected'}
                      </p>
                    )}
                    {!ticketless &&
                      !isVoidWindow &&
                      selectedTicketNumbers.size < (refundQuoteData.tickets?.length ?? 0) && (
                        <p className="text-xs text-amber-600 font-medium">
                          Partial cancellation: {selectedTicketNumbers.size} of {refundQuoteData.tickets?.length ?? 0}{' '}
                          tickets selected. The Chappie payment plan will NOT be auto-terminated — if this is really a
                          full cancellation, select every ticket instead.
                        </p>
                      )}
                    {processCancellationError && <p className="text-sm text-red-600">{processCancellationError}</p>}
                    {waiverCode.trim() && (
                      <p className="text-xs text-amber-700">
                        Waiver code: <span className="font-mono font-medium">{waiverCode.trim()}</span>
                      </p>
                    )}

                    {/* Email overrides */}
                    <div className="space-y-2 pt-1">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="send-supplier-email"
                            checked={sendSupplierEmail}
                            onChange={e => setSendSupplierEmail((e.target as HTMLInputElement).checked)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-amber-600"
                          />
                          <label
                            htmlFor="send-supplier-email"
                            className="text-xs font-medium text-amber-800"
                          >
                            Supplier email
                          </label>
                        </div>
                        <input
                          type="text"
                          value={supplierEmailOverride}
                          onChange={e => setSupplierEmailOverride((e.target as HTMLInputElement).value)}
                          onBlur={() => {
                            if (!supplierEmailOverride.trim()) setSupplierEmailOverride(supplierEmailDefault)
                          }}
                          disabled={!sendSupplierEmail}
                          className="w-full text-xs px-2 py-1 border border-gray-300 rounded font-mono disabled:opacity-40 disabled:bg-gray-50"
                        />
                        {sendSupplierEmail && !supplierEmailOverride.trim() && (
                          <p className="text-xs text-red-600">
                            No supplier address on file for this booking — notification cannot be sent until one is
                            entered.
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="send-customer-email"
                            checked={sendCustomerEmail}
                            onChange={e => setSendCustomerEmail((e.target as HTMLInputElement).checked)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-amber-600"
                          />
                          <label
                            htmlFor="send-customer-email"
                            className="text-xs font-medium text-amber-800"
                          >
                            Customer email
                          </label>
                        </div>
                        <input
                          type="text"
                          value={customerEmailOverride}
                          onChange={e => setCustomerEmailOverride((e.target as HTMLInputElement).value)}
                          disabled={!sendCustomerEmail}
                          className="w-full text-xs px-2 py-1 border border-gray-300 rounded font-mono disabled:opacity-40 disabled:bg-gray-50"
                        />
                      </div>
                      <div className="space-y-1 pt-1 border-t border-amber-200">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="no-refund-expected"
                            checked={noRefundExpected}
                            onChange={e => setNoRefundExpected((e.target as HTMLInputElement).checked)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-amber-600"
                          />
                          <label
                            htmlFor="no-refund-expected"
                            className="text-xs font-medium text-amber-800"
                          >
                            No refund expected
                          </label>
                        </div>
                        <p className="text-xs text-amber-700">
                          Tick for a default or loss-recovery cancellation. No refund request goes to the supplier and
                          the booking skips refund monitoring, so it closes now instead of parking for weeks.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={
                          processCancellationLoading ||
                          voidWindowExpired ||
                          awaitingFareRules ||
                          (!ticketless && selectedTicketNumbers.size === 0) ||
                          !cancellationReason ||
                          !supplierNotificationReason ||
                          requiresManualDeductions ||
                          (sendSupplierEmail && !supplierEmailOverride.trim())
                        }
                        onClick={async () => {
                          if (!quote || !statement || !deductions || voidWindowClosedNow()) return
                          sendConfidence('fallback')
                          setProcessCancellationError('')
                          setProcessCancellationLoading(true)
                          try {
                            const ticketNumbers = refundQuoteData.tickets
                              ? refundQuoteData.tickets
                                  .filter(t => selectedTicketNumbers.has(t.number))
                                  .map(t => t.number)
                              : []

                            const cancellationType = automatedCancellationType

                            // Legacy field names kept for one release (workflow v2 in flight);
                            // the statement block is authoritative for v3 consumers (SPEC.md §7).
                            const financialsPayload = {
                              currency,
                              salesPrice: parseFloat(salesPrice) || 0,
                              totalPaidAmount: parseFloat(totalPaidAmount) || 0,
                              totalNetFare: quote.nettPaid.total,
                              airlineCancellationFee: deductions.total,
                              adminFee: deductions.adminFee,
                              pltBalance: roundCurrency(statement.refundToPax + quote.pltFee),
                              consolidatorFee: quote.supplierFee,
                              netRefund: statement.paxLedger.raw,
                              terminationFee: quote.pltFee,
                              airlineCashRefund: quote.grossAirlineRefund,
                              supplierCashRefundOverridden: isSupplierCashRefundOverridden,
                              refundableTaxAmount: refundableTaxAmount || undefined,
                              statement: {
                                nettPaidToSupplier: quote.nettPaid.total,
                                nettPaidBasis: quote.nettPaid.basis,
                                deductions: {
                                  penalty: deductions.penalty,
                                  nonRefundableTaxes: deductions.nonRefundableTaxes,
                                  adminFee: deductions.adminFee,
                                  total: deductions.total,
                                  source: deductions.source,
                                },
                                grossAirlineRefund: quote.grossAirlineRefund,
                                supplierFee: quote.supplierFee,
                                expectedSupplierRefund: statement.expectedSupplierRefund,
                                pltFee: quote.pltFee,
                                refundToPax: statement.refundToPax,
                                margin: statement.margin,
                                isShortfall: statement.isShortfall,
                              },
                            }

                            const resp = await fetch(`${config.apiBaseUrl}/bookings/${bookingId}/cancel-execution`, {
                              method: 'POST',
                              credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                cancellationType,
                                ticketNumbers,
                                cancellationReason,
                                supplierNotificationReason,
                                financials: financialsPayload,
                                noTicketsIssued: refundQuoteData.noTicketsIssued ?? false,
                                lccBooking: refundQuoteData.lccBooking ?? false,
                                pnr: refundQuoteData.pnr,
                                supplierEmailOverride: sendSupplierEmail ? supplierEmailOverride || null : null,
                                customerEmailOverride: sendCustomerEmail ? customerEmailOverride || null : null,
                                waiverCode: waiverCode.trim() || undefined,
                                noRefundExpected,
                              }),
                            })

                            if (!resp.ok) {
                              const raw = await resp.text().catch(() => '')
                              let parsed: {
                                error?: string
                                details?: { path?: (string | number)[]; message?: string }[]
                              } = {}
                              try {
                                parsed = JSON.parse(raw)
                              } catch {
                                // non-JSON body — a middleware fault answers text/plain, keep it readable
                              }
                              const details = parsed.details
                                ?.map(d => `${(d.path ?? []).join('.')}: ${d.message}`)
                                .join('; ')
                              const reason = [parsed.error ?? raw.trim().slice(0, 300), details]
                                .filter(Boolean)
                                .join(' — ')
                              throw new Error(`Request failed (${resp.status})${reason ? `: ${reason}` : ''}`)
                            }

                            setSubmittedCancellationType(cancellationType)
                            setSubmittedNoRefundExpected(noRefundExpected)
                            setProcessCancellationSuccess(true)
                            setProcessCancellationConfirm(false)
                          } catch (err) {
                            setProcessCancellationError(err instanceof Error ? err.message : 'Unknown error')
                          } finally {
                            setProcessCancellationLoading(false)
                          }
                        }}
                        className="flex-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {processCancellationLoading ? 'Processing…' : 'Confirm — Process Cancellation'}
                      </button>
                      <button
                        type="button"
                        disabled={processCancellationLoading}
                        onClick={() => {
                          setProcessCancellationConfirm(false)
                          setProcessCancellationError('')
                        }}
                        className="px-3 py-2 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setProcessCancellationConfirm(true)}
                    disabled={
                      awaitingFareRules || voidWindowExpired || (!ticketless && selectedTicketNumbers.size === 0)
                    }
                    className="w-full px-4 py-2 bg-red-600 text-white font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {awaitingFareRules
                      ? 'Waiting for fare rules…'
                      : selectedTicketNumbers.size < (refundQuoteData.tickets?.length ?? 0) &&
                          selectedTicketNumbers.size > 0
                        ? `Process Partial Cancellation (${selectedTicketNumbers.size} ticket${selectedTicketNumbers.size !== 1 ? 's' : ''})`
                        : 'Process Cancellation'}
                  </button>
                )}
              </div>
            ) : undefined}

            {/* Copy Quote */}
            <div className="pt-2">
              <button
                type="button"
                onClick={copyQuoteToClipboard}
                disabled={
                  awaitingFareRules ||
                  voidWindowExpired ||
                  !bookingId ||
                  !salesPrice ||
                  !cancellationReason ||
                  (quote?.cancellingPax ?? 0) <= 0
                }
                className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {copySuccess ? (
                  <>
                    <svg
                      className="h-4 w-4 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Copied to Clipboard!
                  </>
                ) : (
                  <>
                    <svg
                      className="h-4 w-4 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                      />
                    </svg>
                    Copy Quote to Clipboard
                  </>
                )}
              </button>
            </div>
          </fieldset>
        )}
      </div>
    </div>
  )
}
